import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import { useForm, Resolver } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
// import { z } from "zod";
// import * as z from "zod";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
  Progress,
  Textarea,
  Badge,
  Input,
  Button,
  Switch,
  Label,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DataTable,
  initialPlans,
  membershipTableConfig,
  Plan,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@b2b/ui-components";
import {
  Upload,
  X,
  Plus,
  Minus,
  Video,
  Image as ImageIcon,
  Save,
  Eye,
  Archive,
  FileText,
  Award,
  Package,
  DollarSign,
  Truck,
  Info,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  User,
  Loader2,
} from "lucide-react";
import { MediaSelector } from "./MediaSelector";
import { SellerSelectionDialog } from "./Admin/SellerSelectionDialog";
import { RichTextEditor } from "./RichTextEditor";
import { CategoryMegaMenuPopupSelector } from "./CategoryMegaMenuPopupSelector";
import { Category } from "./CategoryMegaMenu";
import { isValidCategoryDepth } from "../utils/categoryUtils";
import {
  DEFAULT_PRODUCT_UNIT,
  getProductUnitLabel,
  getProductUnitSelectOptions,
} from "../config/productUnitOptions";
import { applySellerIndustryCategoryFilter } from "../utils/industryCategoryUtils";
import { blockDelta } from "quill/blots/block";

function sanitizeTierQuantityInput(raw: string): string {
  return raw.replace(/\D/g, "");
}

const PRICE_DECIMAL_PLACES = 2;
const PRICE_DRAFT_MAIN = "main";
const MOQ_DRAFT_KEY = "moq";

function priceDraftKeyTier(index: number): string {
  return `tier-${index}`;
}

function sanitizeTierPriceInput(
  raw: string,
  maxDecimals: number = PRICE_DECIMAL_PLACES,
): string {
  let out = "";
  let dotSeen = false;
  for (const ch of raw) {
    if (ch >= "0" && ch <= "9") out += ch;
    else if (ch === "." && !dotSeen) {
      out += ".";
      dotSeen = true;
    }
  }
  const dot = out.indexOf(".");
  if (dot >= 0 && maxDecimals >= 0 && out.length - dot - 1 > maxDecimals) {
    out = out.slice(0, dot + 1 + maxDecimals);
  }
  return out;
}

function roundProductPrice(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** PRICE_DECIMAL_PLACES;
  return Math.round(value * factor) / factor;
}

function toTrimmedString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function tierPriceFromSanitized(sanitized: string): number {
  if (sanitized === "" || sanitized === ".") return 0;
  const n = parseFloat(sanitized);
  return Number.isFinite(n) ? n : 0;
}

/** Map API / JSON tier rows into form shape { quantity, price } (handles string, object-map, aliases). */
function normalizeTieredPricingFromApi(
  raw: unknown,
): { quantity: number; price: number }[] {
  if (raw == null || raw === "") return [];

  let data: unknown = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          /* keep inner string */
        }
      }
    } catch {
      return [];
    }
  }

  let list: unknown[] = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (data && typeof data === "object") {
    list = Object.values(data as Record<string, unknown>);
  } else {
    return [];
  }

  const rows: { quantity: number; price: number }[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const qRaw = o.quantity ?? o.unit ?? o.qty ?? o.minQuantity;
    const pRaw = o.price ?? o.unitPrice;
    if (
      qRaw === undefined ||
      qRaw === null ||
      pRaw === undefined ||
      pRaw === null
    ) {
      continue;
    }
    const quantity = Number(qRaw);
    const price = Number(pRaw);
    if (!Number.isFinite(quantity) || !Number.isFinite(price)) continue;
    rows.push({ quantity, price });
  }
  return rows;
}

/** API payload: only rows with both numeric quantity & price (drops empty editor rows). */
function serializeTieredPricingForPayload(
  rows: { quantity?: number; price?: number }[] | undefined | null,
): { quantity: number; price: number }[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const out: { quantity: number; price: number }[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const quantity = Number(r.quantity);
    const price = roundProductPrice(Number(r.price));
    if (!Number.isFinite(quantity) || !Number.isFinite(price)) continue;
    out.push({ quantity, price });
  }
  return out;
}

const PRODUCT_NAME_MAX_LENGTH = 60;
const PRODUCT_META_DESCRIPTION_MAX_LENGTH = 150;

function applySeoFieldsToPayload<T extends Record<string, unknown>>(data: T): T {
  const metaKeywordsText = resolveMetaKeywordsTextFromPayload(data);
  const metaKeywords = parseMetaKeywordsList(metaKeywordsText);
  const name = String(data.name ?? "")
    .trim()
    .slice(0, PRODUCT_NAME_MAX_LENGTH);
  const metaDescription = String(data.metaDescription ?? "")
    .trim()
    .slice(0, PRODUCT_META_DESCRIPTION_MAX_LENGTH);
  return {
    ...data,
    name,
    metaTitle: name,
    metaDescription: metaDescription || undefined,
    metaKeywordsText,
    metaKeywords,
    keywords: metaKeywords,
  } as unknown as T;
}

function resolveMetaKeywordsTextFromPayload(data: {
  metaKeywordsText?: string;
  metaKeywords?: string[];
  keywords?: string[];
}): string {
  const text = String(data.metaKeywordsText ?? "").trim();
  if (text) return text;
  if (Array.isArray(data.metaKeywords) && data.metaKeywords.length > 0) {
    return data.metaKeywords.join(", ");
  }
  if (Array.isArray(data.keywords) && data.keywords.length > 0) {
    return data.keywords.join(", ");
  }
  return "";
}

function parseMetaKeywordsList(raw: string): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean);
}

const productSchema = z.object({
  // Basic Information - Required fields for API
  name: z
    .string()
    .min(1, "Product name is required")
    .max(
      PRODUCT_NAME_MAX_LENGTH,
      `Product name must be ${PRODUCT_NAME_MAX_LENGTH} characters or less`,
    ),
  description: z.string().min(1, "Description is required"),
  sku: z.string().optional(), // Deprecated but still used in code
  price: z.number().min(0, "Price must be positive").default(0), // Add price field
  // DEPRECATED: cost: z.number().min(0, "Cost must be positive").optional().default(0), // Deprecated
  weight: z.number().min(0, "Weight must be positive").optional().default(0), // Add weight field
  dimensions: z.string().optional(), // Deprecated but still used in code
  stock: z.number().min(0, "Stock must be non-negative").optional().default(0), // Deprecated but still used in code
  minStock: z
    .number()
    .min(0, "Min stock must be non-negative")
    .optional()
    .default(0), // Deprecated but still used in code
  images: z
    .array(z.string())
    .min(1, "At least one gallery image is required")
    .max(5, "Maximum 5 gallery images allowed"),
  thumbnailImage: z.string().optional(), // Optional: first gallery image used if not provided
  pdfSpecification: z.string().optional(),
  videoUrl: z.string().optional(),
  documents: z.array(z.string()).default([]),
  certifications: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),

  // Keep existing fields
  // For three-level categories, either category (name) or categoryId is required
  category: z.string().optional(),
  categoryId: z.string().min(1, "Category is required"),
  subcategory: z.string().default(""),
  hsCode: z.string().optional(), // Deprecated but still used in code
  brand: z.string().default(""),
  model: z.string().optional(), // Deprecated but still used in code
  unitOfMeasure: z.string().optional().default(DEFAULT_PRODUCT_UNIT),
  keywords: z.array(z.string()).optional().default([]), // Deprecated but still used in code

  // Pricing & Quantity - Keep existing but add price field
  priceMin: z
    .number()
    .positive("Minimum price must be greater than 0")
    .optional(), // Deprecated but still used in code
  priceMax: z
    .number()
    .positive("Maximum price must be greater than 0")
    .optional(), // Deprecated but still used in code
  currency: z.string().default("USD"),
  moq: z
    .number({ message: "Minimum order quantity is required" })
    .int("MOQ must be a whole number")
    .min(1, "MOQ must be at least 1"),
  moqUnit: z.string().optional().default("pieces"),
  supplyAbilityPerMonth: z.string().optional(), // Deprecated but still used in code
  tieredPricing: z
    .array(z.object({ quantity: z.number(), price: z.number() }))
    .default([]),
  // Tiered pricing base fields
  tieredMoq: z.number().min(1, "MOQ must be at least 1").optional(),
  tieredPrice: z.number().min(0, "Price must be positive").optional(),
  paymentTerms: z.array(z.string()).default([]),

  // Packaging & Logistics
  packaging: z.string().default(""),
  logisticsModes: z.array(z.string()).default([]),
  shippingPort: z.string().default(""),
  deliveryTime: z.string().default(""),
  // DEPRECATED: packageDetails: z.string().default(""), // Deprecated
  fobPrice: z.boolean().default(false),
  cifPrice: z.boolean().default(false),
  exportMarkets: z.array(z.string()).default([]),

  // Additional Info
  productUrl: z.string().default(""),
  productId: z.string().default(""),
  productGroup: z.string().default(""),
  warrantyInfo: z.string().default(""),
  serviceInfo: z.string().default(""),
  status: z.string().default("draft"),
  isEnabled: z.boolean().default(true),
  assignedSellerId: z.string().optional(),
  companyId: z.string().min(1, "Company ID is required"), // Add companyId field

  // New product specification fields
  materialType: z.string().optional(),
  size: z.string().optional(),
  color: z.string().optional(),
  grade: z.string().optional(), // Deprecated but still used in code
  shelfLife: z.string().optional(),
  otherSpecification: z.string().optional(), // Deprecated but still used in code

  // External link and SEO fields
  // DEPRECATED: externalLink - removed from UI, kept for backward compatibility
  externalLink: z.string().url().optional().or(z.literal("")),
  // DEPRECATED: externalLinkButtonText - removed from UI, kept for backward compatibility
  externalLinkButtonText: z.string().optional(),
  metaTitle: z
    .string()
    .max(
      PRODUCT_NAME_MAX_LENGTH,
      `Meta title must be ${PRODUCT_NAME_MAX_LENGTH} characters or less`,
    )
    .optional(),
  metaDescription: z
    .string()
    .max(
      PRODUCT_META_DESCRIPTION_MAX_LENGTH,
      `Meta description must be ${PRODUCT_META_DESCRIPTION_MAX_LENGTH} characters or less`,
    )
    .optional(),
  metaKeywords: z.array(z.string()).optional().default([]),
  /** Comma-separated display value for meta keywords (synced with metaKeywords on edit). */
  metaKeywordsText: z.string().optional().default(""),

  // Discount fields - Deprecated but still used in code
  discount: z.number().min(0, "Discount must be positive").optional(),
  discountStartDate: z.date().optional(),
  discountEndDate: z.date().optional(),
  discountQuantity: z
    .number()
    .min(1, "Discount quantity must be at least 1")
    .optional(),
});
// Category requirement is enforced by categoryId only (avoid duplicate "Category is required" from a second refine on `category`).

type ProductFormData = z.infer<typeof productSchema>;

interface ProductDialogProps {
  trigger?: React.ReactNode;
  /** API product may include id/updatedAt for edit-mode form hydration (not part of zod form schema). */
  product?: Partial<ProductFormData> & {
    id?: string;
    updatedAt?: string | Date;
  };
  isEdit?: boolean;
  isAdminMode?: boolean;
  companyId?: string; // Add companyId prop
  /** Seller-only: root industry category IDs from business profile (industryTypes). */
  allowedCategoryIds?: string[] | undefined;
  /** When true (non-edit), blocks step advance and save/publish (product quota / membership). */
  lockWizard?: boolean;
  onSave?: (data: ProductFormData) => void;
  onUpdate?: (data: ProductFormData) => void;
  onSaveDraft?: (data: ProductFormData) => void;
  onPublish?: (data: ProductFormData) => void;
  onArchive?: () => void;
  // New props for controlled dialog
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean; // Loading state from parent
  renderAsPage?: boolean; // New prop to render as full page instead of dialog
  // Props to expose footer state and actions for page mode
  onFooterStateChange?: (state: {
    currentStep: number;
    canGoNext: boolean;
    canGoPrev: boolean;
    isPublishEnabled: boolean;
    totalSteps: number;
    /** When false, parent should hide the Next button (e.g. when on basic step and description is empty) */
    showNextButton?: boolean;
  }) => void;
  // Expose handlers for parent to call
  footerHandlers?: {
    onPrev: () => void;
    onNext: () => void;
    onSaveDraft: () => void;
    onPublish: () => void;
    onArchive?: () => void;
  };
}

interface CategoryNode {
  id: string;
  name: string;
  parentId?: string;
  isActive?: boolean;
  active?: boolean;
  status?: string;
  children?: CategoryNode[];
}
/** When saving a draft, clear only errors from other wizard steps (avoid full clearErrors(), which can prevent field errors from re-rendering after trigger). */
const DRAFT_SAVE_CLEAR_ERRORS: Record<string, (keyof ProductFormData)[]> = {
  "seller-assignment": [
    "name",
    "categoryId",
    "description",
    "price",
    "paymentTerms",
    "shippingPort",
    "packaging",
    "images",
  ],
  basic: ["price", "paymentTerms", "shippingPort", "packaging", "images"],
  pricing: [
    "name",
    "categoryId",
    "description",
    "shippingPort",
    "packaging",
    "images",
  ],
  logistics: [
    "name",
    "categoryId",
    "description",
    "price",
    "paymentTerms",
    "images",
  ],
  media: [
    "name",
    "categoryId",
    "description",
    "price",
    "paymentTerms",
    "shippingPort",
    "packaging",
  ],
  review: [],
};

const getSteps = (isAdminMode: boolean = false) => {
  const baseSteps = [
    { id: "basic", title: "Basic Info", icon: Info },
    { id: "pricing", title: "Pricing & Quantity", icon: DollarSign },
    { id: "logistics", title: "Packaging & Logistics", icon: Truck },
    { id: "media", title: "Media Uploads", icon: ImageIcon },
    { id: "review", title: "Review & Submit", icon: CheckCircle },
  ];

  if (isAdminMode) {
    return [
      { id: "seller-assignment", title: "Assign to Seller", icon: User },
      ...baseSteps,
    ];
  }

  return baseSteps;
};

export function StoreProductDialog({
  trigger,
  product,
  isEdit = false,
  isAdminMode = false,
  companyId,
  allowedCategoryIds,
  lockWizard = false,
  onSave,
  onUpdate,
  onSaveDraft,
  onPublish,
  onArchive,
  open: controlledOpen,
  onOpenChange,
  isLoading = false,
  renderAsPage = false,
  onFooterStateChange,
  footerHandlers,
}: ProductDialogProps) {
  const wizardLocked = Boolean(lockWizard && !isEdit);

  const [internalOpen, setInternalOpen] = useState(false);

  // Ref to track if form has been initialized (prevents clearing user input on step navigation)
  const formInitializedRef = useRef(false);
  /** Page layout: inner scroll container for step form (Create Product uses nested overflow-y-auto). */
  const pageFormScrollRef = useRef<HTMLDivElement>(null);
  /** Page layout: step title + progress strip — scroll into view on step change. */
  const pageStepHeaderRef = useRef<HTMLDivElement>(null);

  // Use controlled open state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }

    // When dialog closes for new products, reset initialization flag
    // This allows form to start fresh when reopening for a new product
    if (!newOpen && !isEdit) {
      formInitializedRef.current = false;
      setCurrentStep(0);
    }
  };
  const setOpen = handleOpenChange;
  const [currentStep, setCurrentStep] = useState(0);
  const [internalLoading, setInternalLoading] = useState(false);
  const [validatedSteps, setValidatedSteps] = useState<Set<string>>(new Set());
  const [currentStepValid, setCurrentStepValid] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<
    Array<{
      id: string;
      url: string;
      thumbnailUrl?: string;
      originalName: string;
    }>
  >([]);
  const [uploadedVideo, setUploadedVideo] = useState<string | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useState<string[]>([]);
  const [uploadedCertifications, setUploadedCertifications] = useState<
    string[]
  >([]);
  const [thumbnailImage, setThumbnailImage] = useState<{
    id: string;
    url: string;
    thumbnailUrl?: string;
    originalName: string;
  } | null>(null);
  const [pdfSpecification, setPdfSpecification] = useState<{
    id: string;
    url: string;
    thumbnailUrl?: string;
    originalName: string;
  } | null>(null);

  const [specifications, setSpecifications] = useState<
    { key: string; value: string }[]
  >([{ key: "", value: "" }]);
  const [currentKeyword, setCurrentKeyword] = useState("");
  const [activeTierField, setActiveTierField] = useState<{
    index: number;
    field: "quantity" | "price";
  } | null>(null);
  /** While typing, keeps raw strings (e.g. "8.") so the decimal point is not lost on re-render. */
  const [priceInputDrafts, setPriceInputDrafts] = useState<
    Record<string, string>
  >({});
  const [selectedSellerId, setSelectedSellerId] = useState<string>("");
  const [selectedSeller, setSelectedSeller] = useState<{
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    company?: { name?: string };
  } | null>(null);
  const [isSellerDialogOpen, setIsSellerDialogOpen] = useState(false);
  const [assignmentType, setAssignmentType] = useState<"seller" | "admin">(
    "seller",
  );
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [rawCategories, setRawCategories] = useState<CategoryNode[]>([]);
  const [areCategoriesLoaded, setAreCategoriesLoaded] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState<string>("");
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const docsInputRef = useRef<HTMLInputElement>(null);
  const certsInputRef = useRef<HTMLInputElement>(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [currentMediaType, setCurrentMediaType] = useState<
    "gallery" | "thumbnail" | "pdf"
  >("gallery");
  /** Persists meta keyword text across wizard step unmounts (Media step). */
  const metaKeywordsTextRef = useRef("");
  const getProductVideoUrl = useCallback(
    (productData: any): string =>
      String(
        productData?.videoUrl ??
          productData?.videoLink ??
          productData?.video_url ??
          "",
      ),
    [],
  );
  const parseMetaKeywords = useCallback((raw: string): string[] => {
    if (!raw) return [];
    return raw
      .split(",")
      .map((keyword) => keyword.trim())
      .filter(Boolean);
  }, []);
  const getProductMetaKeywordsArray = useCallback(
    (productData: any): string[] => {
      const directText = String(productData?.metaKeywordsText ?? "").trim();
      if (directText) {
        return parseMetaKeywordsList(directText);
      }
      const source =
        productData?.metaKeywords ??
        productData?.meta_keywords ??
        productData?.keywords ??
        [];
      if (Array.isArray(source)) {
        return source.map((item) => String(item).trim()).filter(Boolean);
      }
      if (typeof source === "string") {
        return parseMetaKeywords(source);
      }
      return [];
    },
    [parseMetaKeywords],
  );
  const getProductMetaKeywordsInput = useCallback(
    (productData: any): string => {
      const directText = String(productData?.metaKeywordsText ?? "").trim();
      if (directText) return directText;
      return getProductMetaKeywordsArray(productData).join(", ");
    },
    [getProductMetaKeywordsArray],
  );

  const withMetaKeywordsPayload = useCallback(
    (data: ProductFormData): ProductFormData => {
      const metaKeywordsText =
        metaKeywordsTextRef.current.trim() ||
        String(data.metaKeywordsText ?? "").trim();
      const metaKeywords = parseMetaKeywordsList(metaKeywordsText);
      return {
        ...data,
        metaKeywordsText,
        metaKeywords,
      };
    },
    [],
  );
  const [originalMediaSelection, setOriginalMediaSelection] = useState<
    Array<{
      id: string;
      url: string;
      thumbnailUrl?: string;
      originalName: string;
    }>
  >([]);

  //   const form = useForm<ProductFormData>({
  //     resolver: zodResolver(productSchema) as Resolver<ProductFormData>,
  //     defaultValues: productSchema.parse(product ?? {}) as ProductFormData,
  //   });

  const typedResolver = zodResolver(
    productSchema,
  ) as unknown as Resolver<ProductFormData>;

  // ✅ Always safe, because schema now has defaults
  //   const form = useForm<ProductFormData>({
  //     resolver: typedResolver,
  //     defaultValues: productSchema.parse(product ?? {}),
  //   });

  const isCategoryRestrictionEnabled =
    !isAdminMode && allowedCategoryIds !== undefined;

  const isCategoryActive = (category: CategoryNode): boolean => {
    if (typeof category.isActive === "boolean") {
      return category.isActive;
    }
    if (typeof category.active === "boolean") {
      return category.active;
    }
    if (typeof category.status === "string") {
      return category.status.toLowerCase() === "active";
    }
    // Default to visible when active metadata is absent.
    return true;
  };

  const filterActiveCategoryTree = (
    inputCategories: CategoryNode[],
  ): CategoryNode[] => {
    return (inputCategories || []).reduce<CategoryNode[]>((acc, category) => {
      if (!isCategoryActive(category)) {
        // If parent is inactive, remove it along with its descendants.
        return acc;
      }

      const nextChildren = Array.isArray(category.children)
        ? filterActiveCategoryTree(category.children)
        : [];

      acc.push({
        ...category,
        children: nextChildren,
      });

      return acc;
    }, []);
  };

  const applyCategoryRestriction = (
    inputCategories: typeof categories,
  ): typeof categories => {
    if (!isCategoryRestrictionEnabled) return inputCategories;

    const allowed = Array.isArray(allowedCategoryIds) ? allowedCategoryIds : [];
    return applySellerIndustryCategoryFilter(
      (inputCategories || []) as CategoryNode[],
      allowed,
    ) as typeof categories;
  };

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setAreCategoriesLoaded(false);

        // Import adminService dynamically to avoid circular dependencies
        const { adminService } = await import(
          "@b2b/services/api/modules/admin/admin.service"
        );
        // Include 3rd-level (Sub product) categories for product create/edit only.
        const data = await adminService.getCategories(true, false, 3);
        console.log("Fetched categories:", data);
        const activeCategories = filterActiveCategoryTree(
          (data || []) as CategoryNode[],
        );
        setRawCategories(activeCategories);
        setCategories(applyCategoryRestriction(activeCategories));
      } catch (error) {
        console.error("Error fetching categories:", error);
        // Fallback: try direct API call
        try {
          const response = await fetch(
            "/api/v1/admin/categories?hierarchy=true&maxDepth=3",
          );
          const result = await response.json();
          console.log("Fallback API response:", result);
          if (result.success) {
            const activeCategories = filterActiveCategoryTree(
              (result.data || []) as CategoryNode[],
            );
            setRawCategories(activeCategories);
            setCategories(applyCategoryRestriction(activeCategories));
          }
        } catch (fallbackError) {
          console.error("Fallback API call failed:", fallbackError);
        }
      } finally {
        setAreCategoriesLoaded(true);
      }
    };
    fetchCategories();
  }, []);

  // Dynamically re-apply category restriction when membership industries change.
  useEffect(() => {
    if (!areCategoriesLoaded) return;

    if (!isCategoryRestrictionEnabled) {
      setCategories(rawCategories);
      return;
    }

    setCategories(applyCategoryRestriction(rawCategories));
  }, [allowedCategoryIds, isAdminMode, areCategoriesLoaded, rawCategories]);

  // Helper functions for category hierarchy
  const getRootCategories = () => {
    const rootCats = categories.filter((cat) => !cat.parentId);
    console.log("Root categories:", rootCats);
    console.log("All categories:", categories);
    return rootCats;
  };

  const getLevel1Children = (parentId: string) => {
    if (!parentId) return [];
    const parent = categories.find((cat) => cat.id === parentId);
    return parent?.children || [];
  };

  const getLevel2Children = (level1Id: string) => {
    if (!level1Id || level1Id === "none") return [];
    // First find the Level 1 category in the main categories array
    const level1Category = categories.find((cat) => cat.id === level1Id);
    if (level1Category) {
      console.log("Level 1 category found in main array:", level1Category);
      console.log("Level 1 children:", level1Category.children);
      return level1Category.children || [];
    }
    // If not found in main array, search in all categories recursively
    for (const category of categories) {
      if (category.children) {
        const found = category.children.find((child) => child.id === level1Id);
        if (found) {
          console.log("Level 1 category found in children:", found);
          console.log("Level 2 children:", found.children);
          return found.children || [];
        }
      }
    }
    console.log("Level 1 category not found for ID:", level1Id);
    return [];
  };

  const handleChooseFile = (
    mediaType: "gallery" | "thumbnail" | "pdf",
  ) => {
    setCurrentMediaType(mediaType);

    // Store the current selection as original
    if (mediaType === "gallery") {
      setOriginalMediaSelection([...uploadedImages]);
    } else if (mediaType === "thumbnail") {
      setOriginalMediaSelection(thumbnailImage ? [thumbnailImage] : []);
    } else if (mediaType === "pdf") {
      setOriginalMediaSelection(pdfSpecification ? [pdfSpecification] : []);
    }

    setIsMediaModalOpen(true);
  };

  const handleMediaSelection = async (
    selectedMediaItems: Array<{
      id: string;
      url: string;
      thumbnailUrl?: string;
      originalName: string;
    }>,
    shouldCloseModal?: boolean,
  ) => {
    // If empty array, it means Cancel was clicked - restore original selection
    if (selectedMediaItems.length === 0) {
      if (currentMediaType === "gallery") {
        setUploadedImages(originalMediaSelection);
      } else if (currentMediaType === "thumbnail") {
        setThumbnailImage(originalMediaSelection[0] || null);
      } else if (currentMediaType === "pdf") {
        setPdfSpecification(originalMediaSelection[0] || null);
      }
      setIsMediaModalOpen(false);
      return;
    }

    // Apply the new selection
    if (currentMediaType === "gallery") {
      setUploadedImages(selectedMediaItems);
      // Sync with form for validation
      form.setValue(
        "images",
        selectedMediaItems.map((item) => item.url),
        { shouldValidate: true },
      );
    } else if (currentMediaType === "thumbnail") {
      const thumbnail = selectedMediaItems[0] || null;
      setThumbnailImage(thumbnail);
      // Sync with form for validation
      if (thumbnail && thumbnail.url) {
        form.setValue("thumbnailImage", thumbnail.url, {
          shouldValidate: true,
        });
      } else {
        form.setValue("thumbnailImage", "", { shouldValidate: true });
      }
    } else if (currentMediaType === "pdf") {
      setPdfSpecification(selectedMediaItems[0] || null);
    }

    // Only close modal if explicitly requested (Add Files button)
    if (shouldCloseModal) {
      setIsMediaModalOpen(false);
    }
  };

  // ARCHITECTURAL REFACTOR: Form created ONCE - stable instance, reset only when product changes
  // Create form with empty defaults initially
  const form = useForm<ProductFormData>({
    resolver: typedResolver,
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      // DEPRECATED: sku: "",
      price: 0,
      // DEPRECATED: cost: 0,
      weight: 0,
      // DEPRECATED: dimensions: "",
      // DEPRECATED: stock: 0,
      // DEPRECATED: minStock: 0,
      images: [],
      videoUrl: "",
      documents: [],
      certifications: [],
      tags: [],
      companyId: companyId || "",
      category: "",
      categoryId: "",
      subcategory: "",
      // DEPRECATED: hsCode: "",
      brand: "",
      // DEPRECATED: model: "",
      unitOfMeasure: "pieces",
      // DEPRECATED: keywords: [],
      // DEPRECATED: priceMin: undefined,
      // DEPRECATED: priceMax: undefined,
      currency: "USD",
      // DEPRECATED: moq: 1,
      // DEPRECATED: moqUnit: "pieces",
      // DEPRECATED: supplyAbilityPerMonth: "",
      tieredPricing: [],
      tieredMoq: undefined,
      tieredPrice: undefined,
      paymentTerms: [],
      packaging: "",
      logisticsModes: [],
      shippingPort: "",
      deliveryTime: "",
      // DEPRECATED: packageDetails: "",
      fobPrice: false,
      cifPrice: false,
      exportMarkets: [],
      productUrl: "",
      productId: "",
      productGroup: "",
      warrantyInfo: "",
      serviceInfo: "",
      status: "draft",
      isEnabled: true,
      assignedSellerId: "",
      // New product specification fields
      materialType: "",
      size: "",
      color: "",
      // DEPRECATED: grade: "",
      shelfLife: "",
      // DEPRECATED: otherSpecification: "",
      // External link and SEO fields
      externalLink: "",
      externalLinkButtonText: "",
      metaTitle: "",
      metaDescription: "",
      metaKeywords: [],
      metaKeywordsText: "",
      // DEPRECATED: Discount fields
      // discount: undefined,
      // discountStartDate: undefined,
      // discountEndDate: undefined,
      // discountQuantity: undefined,
    },
    shouldUnregister: false,
    shouldFocusError: false,
  });

  // ROBUST FIX: Only reset form when dialog FIRST opens with valid data
  // Never reset if user has made changes (form is dirty)
  const initializationRef = useRef<{
    productKey: string | null;
    dialogOpen: boolean;
    initialized: boolean;
    formInstanceKey: string;
  }>({
    productKey: null,
    dialogOpen: false,
    initialized: false,
    formInstanceKey: `form-${Date.now()}-${Math.random()}`,
  });

  // Create stable form instance key for field keys
  // This ensures fields maintain identity when unmounting/remounting during step navigation
  const formInstanceKey = initializationRef.current.formInstanceKey;

  // CRITICAL: Initialize form when dialog opens - use ref to track initialization state
  // This ensures form fills consistently every time dialog opens
  useEffect(() => {
    // If dialog is closed, reset tracking but DON'T reset form (preserve for next open)
    if (!open) {
      initializationRef.current.dialogOpen = false;
      // Don't reset initialized flag - keep it for next time dialog opens
      return;
    }

    // Dialog is now open
    const wasDialogOpen = initializationRef.current.dialogOpen;
    initializationRef.current.dialogOpen = true;

    // Only initialize when dialog FIRST opens (not on re-renders)
    const dialogJustOpened = !wasDialogOpen;

    // For new products: only reset when dialog first opens
    if (!isEdit) {
      if (dialogJustOpened) {
        initializationRef.current.initialized = true;
        initializationRef.current.productKey = null;
        metaKeywordsTextRef.current = "";

        // Reset to empty form immediately when dialog first opens
        form.reset(
          {
            name: "",
            description: "",
            // DEPRECATED: sku: "",
            price: 0,
            // DEPRECATED: cost: 0,
            weight: 0,
            // DEPRECATED: dimensions: "",
            // DEPRECATED: stock: 0,
            // DEPRECATED: minStock: 0,
            images: [],
            videoUrl: "",
            documents: [],
            certifications: [],
            tags: [],
            companyId: companyId || "",
            category: "",
            categoryId: "",
            subcategory: "",
            // DEPRECATED: hsCode: "",
            brand: "",
            // DEPRECATED: model: "",
            unitOfMeasure: "pieces",
            // DEPRECATED: keywords: [],
            priceMin: undefined,
            priceMax: undefined,
            currency: "USD",
            // DEPRECATED: moq: 1,
            // DEPRECATED: moqUnit: "pieces",
            // DEPRECATED: supplyAbilityPerMonth: "",
            tieredPricing: [],
            tieredMoq: undefined,
            tieredPrice: undefined,
            paymentTerms: [],
            packaging: "",
            logisticsModes: [],
            shippingPort: "",
            deliveryTime: "",
            // DEPRECATED: packageDetails: "",
            fobPrice: false,
            cifPrice: false,
            exportMarkets: [],
            productUrl: "",
            productId: "",
            productGroup: "",
            warrantyInfo: "",
            serviceInfo: "",
            status: "draft",
            isEnabled: true,
            assignedSellerId: "",
            // New product specification fields
            materialType: "",
            size: "",
            color: "",
            // DEPRECATED: grade: "",
            shelfLife: "",
            // DEPRECATED: otherSpecification: "",
            // External link and SEO fields
            externalLink: "",
            externalLinkButtonText: "",
            metaTitle: "",
            metaDescription: "",
            metaKeywords: [],
            metaKeywordsText: "",
            // Discount fields
            discount: undefined,
            discountStartDate: undefined,
            discountEndDate: undefined,
            discountQuantity: undefined,
          },
          {
            keepErrors: false,
            keepDirty: false,
            keepIsSubmitted: false,
            keepIsValid: false,
            keepSubmitCount: false,
          },
        );
      }
      return;
    }

    // For edit mode: only reset when dialog first opens OR product actually changed
    if (!product) return;

    // Create stable product key from name, sku, and saved keywords
    const currentProductKey = `${product.name || ""}_${product.sku || ""}_${getProductMetaKeywordsInput(product)}`;
    const productChanged =
      initializationRef.current.productKey !== currentProductKey;

    // CRITICAL: Only reset if:
    // 1. Dialog just opened (always fill on open), OR
    // 2. Product actually changed AND form is not dirty
    // NEVER reset if user has made changes (form is dirty) unless product changed
    const formIsDirty = form.formState.isDirty;
    const shouldReset = dialogJustOpened || (productChanged && !formIsDirty);

    if (shouldReset) {
      initializationRef.current.productKey = currentProductKey;
      initializationRef.current.initialized = true;

      // Reset form with product data immediately
      form.reset(
        {
          name: product.name || "",
          description: product.description || "",
          sku: product.sku || "",
          price: typeof product.price === "number" ? product.price : 0,
          // DEPRECATED: cost: typeof product.cost === "number" ? product.cost : 0,
          weight: typeof product.weight === "number" ? product.weight : 0,
          dimensions: product.dimensions || "",
          stock: typeof product.stock === "number" ? product.stock : 0,
          minStock: typeof product.minStock === "number" ? product.minStock : 0,
          images: Array.isArray(product.images) ? product.images : [],
          videoUrl: getProductVideoUrl(product),
          documents: Array.isArray(product.documents) ? product.documents : [],
          certifications: Array.isArray(product.certifications)
            ? product.certifications
            : [],
          tags: Array.isArray(product.tags) ? product.tags : [],
          companyId: product.companyId || companyId || "",
          category: product.category || "",
          categoryId: product.categoryId || "",
          subcategory: product.subcategory || "",
          hsCode: product.hsCode || "",
          brand: product.brand || "",
          model: product.model || "",
          unitOfMeasure: product.unitOfMeasure || "pieces",
          keywords: Array.isArray(product.keywords) ? product.keywords : [],
          priceMin:
            typeof product.priceMin === "number" && product.priceMin > 0
              ? product.priceMin
              : undefined,
          priceMax:
            typeof product.priceMax === "number" && product.priceMax > 0
              ? product.priceMax
              : undefined,
          currency: product.currency || "USD",
          moq:
            typeof product.moq === "number" && product.moq >= 1
              ? product.moq
              : undefined,
          moqUnit: product.moqUnit || product.unitOfMeasure || "pieces",
          supplyAbilityPerMonth: product.supplyAbilityPerMonth || "",
          tieredPricing: normalizeTieredPricingFromApi(product.tieredPricing),
          paymentTerms: Array.isArray(product.paymentTerms)
            ? product.paymentTerms
            : [],
          packaging: product.packaging || "",
          logisticsModes: Array.isArray(product.logisticsModes)
            ? product.logisticsModes
            : [],
          shippingPort: product.shippingPort || "",
          deliveryTime: product.deliveryTime || "",
          // DEPRECATED: packageDetails: product.packageDetails || "",
          fobPrice: product.fobPrice || false,
          cifPrice: product.cifPrice || false,
          exportMarkets: Array.isArray(product.exportMarkets)
            ? product.exportMarkets
            : [],
          productUrl: product.productUrl || "",
          productId: product.productId || "",
          productGroup: product.productGroup || "",
          warrantyInfo: product.warrantyInfo || "",
          serviceInfo: product.serviceInfo || "",
          status: product.status || "draft",
          isEnabled: product.isEnabled ?? true,
          assignedSellerId: product.assignedSellerId || "",
          // New product specification fields
          materialType: product.materialType || "",
          size: product.size || "",
          color: product.color || "",
          // DEPRECATED: grade: product.grade || "",
          shelfLife: product.shelfLife || "",
          // DEPRECATED: otherSpecification: product.otherSpecification || "",
          // External link and SEO fields
          externalLink: product.externalLink || "",
          externalLinkButtonText: product.externalLinkButtonText || "",
          metaTitle: product.metaTitle || product.name || "",
          metaDescription: product.metaDescription || "",
          metaKeywords: getProductMetaKeywordsArray(product),
          metaKeywordsText: getProductMetaKeywordsInput(product),
          // Discount fields
          discount:
            typeof product.discount === "number" ? product.discount : undefined,
          discountStartDate: product.discountStartDate
            ? new Date(product.discountStartDate)
            : undefined,
          discountEndDate: product.discountEndDate
            ? new Date(product.discountEndDate)
            : undefined,
          discountQuantity:
            typeof product.discountQuantity === "number"
              ? product.discountQuantity
              : undefined,
        },
        {
          keepErrors: false,
          keepDirty: false,
          keepIsSubmitted: false,
          keepIsValid: false,
          keepSubmitCount: false,
        },
      );
      metaKeywordsTextRef.current = getProductMetaKeywordsInput(product);
    }
    // CRITICAL: Dependencies - open, product identity, and payload freshness (id/updatedAt).
    // tieredPricing can arrive as array, JSON string, or object map — normalize on reset only.
    // DO NOT include form.formState.isDirty in deps - it causes effect to run on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isEdit,
    product?.id,
    product?.name,
    product?.sku,
    product?.updatedAt,
    companyId,
    getProductMetaKeywordsArray,
    getProductMetaKeywordsInput,
  ]);

  // Set selected seller when editing a product with assignedSellerId
  // Only set if assignmentType is "seller" (not "admin")
  useEffect(() => {
    if (isEdit && product?.assignedSellerId && assignmentType === "seller") {
      const productWithSeller = product as any;
      if (productWithSeller.assignedSeller) {
        const seller = productWithSeller.assignedSeller;
        setSelectedSellerId(product.assignedSellerId);
        setSelectedSeller({
          id: product.assignedSellerId,
          firstName: seller.firstName,
          lastName: seller.lastName,
          email: seller.email || "",
          company: seller.company ? { name: seller.company.name } : undefined,
        });
      } else {
        // If we have assignedSellerId but no seller object, just set the ID
        // The dialog will handle displaying it when opened
        setSelectedSellerId(product.assignedSellerId);
      }
    } else if (
      !isEdit ||
      !product?.assignedSellerId ||
      assignmentType === "admin"
    ) {
      // Reset when creating new product, product has no assigned seller, or assignmentType is admin
      setSelectedSellerId("");
      setSelectedSeller(null);
    }
  }, [isEdit, product?.assignedSellerId, assignmentType]);

  // Keep assignment type in sync with edited product ownership.
  // Products without assignedSellerId are treated as admin-owned.
  useEffect(() => {
    if (!open) return;

    if (isEdit) {
      setAssignmentType(product?.assignedSellerId ? "seller" : "admin");
      return;
    }

    // Preserve existing create flow default.
    setAssignmentType("seller");
  }, [open, isEdit, product?.id, product?.assignedSellerId]);

  // Clear selectedSellerId when assignmentType changes to "admin"
  useEffect(() => {
    if (assignmentType === "admin") {
      setSelectedSellerId("");
      setSelectedSeller(null);
    }
  }, [assignmentType]);

  // Helper function to find category and set dropdown states
  const findCategoryAndSetStates = useCallback(
    (targetId: string) => {
      if (!targetId || categories.length === 0) return;

      // Check if it's a root category
      const rootCategory = categories.find((cat) => cat.id === targetId);
      if (rootCategory) {
        setSelectedParentId(targetId);
        setSelectedChildId("");
        return;
      }

      // Check if it's a level 1 or level 2 category
      for (const rootCat of categories) {
        if (rootCat.children) {
          // Check level 1 categories
          for (const level1Category of rootCat.children) {
            if (level1Category.id === targetId) {
              setSelectedParentId(rootCat.id);
              setSelectedChildId(targetId);
              return;
            }

            // Check level 2 and level 3 categories
            if (level1Category.children) {
              for (const level2Category of level1Category.children) {
                if (level2Category.id === targetId) {
                  setSelectedParentId(rootCat.id);
                  setSelectedChildId(level1Category.id);
                  return;
                }
                if (level2Category.children) {
                  for (const level3Category of level2Category.children) {
                    if (level3Category.id === targetId) {
                      setSelectedParentId(rootCat.id);
                      setSelectedChildId(level1Category.id);
                      return;
                    }
                  }
                }
              }
            }
          }
        }
      }
    },
    [categories],
  );

  // Category change handlers - must be defined after form
  const handleParentChange = useCallback(
    (parentId: string) => {
      if (!parentId) return;
      setSelectedParentId(parentId);
      setSelectedChildId("");

      // Check if parent has children
      const parent = categories.find((cat) => cat.id === parentId);
      const level1Children = parent?.children || [];

      if (level1Children.length > 0) {
        // Parent has children - require selection of a child category
        // Don't set categoryId yet, wait for child selection
        form.setValue("categoryId", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
        form.setValue("category", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        // Parent has no children - use parent directly
        const categoryName = parent?.name || "";
        form.setValue("categoryId", parentId, {
          shouldDirty: true,
          shouldValidate: true,
        });
        form.setValue("category", categoryName, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [categories, form],
  );

  const handleLevel1ChildChange = useCallback(
    (childId: string) => {
      if (!selectedParentId || !childId) return;

      setSelectedChildId(childId);

      // Use Level 1 child category (required, so no "none" option)
      const finalCategoryId = childId;
      const categoryName =
        categories.find((cat) => cat.id === childId)?.name || "";

      form.setValue("categoryId", finalCategoryId, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue("category", categoryName, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [selectedParentId, categories, form],
  );

  const handleLevel2ChildChange = useCallback(
    (childId: string) => {
      if (!selectedChildId) return;

      let finalCategoryId: string;
      let categoryName: string;

      if (childId === "none") {
        // Use Level 1 category
        finalCategoryId = selectedChildId;
        categoryName =
          categories.find((cat) => cat.id === selectedChildId)?.name || "";
      } else {
        // Use Level 2 child category
        finalCategoryId = childId;
        categoryName = categories.find((cat) => cat.id === childId)?.name || "";
      }

      form.setValue("categoryId", finalCategoryId, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue("category", categoryName, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [selectedChildId, categories, form],
  );

  // Unified handler for category selection from mega menu popup
  const handleCategorySelect = useCallback(
    (categoryId: string, categoryPath: Category[]) => {
      // Get category name from path (last item in path)
      const categoryName =
        categoryPath.length > 0
          ? categoryPath[categoryPath.length - 1].name
          : "";

      // Update form values
      form.setValue("categoryId", categoryId, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue("category", categoryName, {
        shouldDirty: true,
        shouldValidate: true,
      });

      // Update dropdown states for backward compatibility
      if (categoryPath.length > 0) {
        const level1Category = categoryPath[0];
        setSelectedParentId(level1Category.id);

        if (categoryPath.length > 1) {
          const level2Category = categoryPath[1];
          setSelectedChildId(level2Category.id);
        } else {
          setSelectedChildId("");
        }
        // Level-3 selection is handled inside CategoryMegaMenuPopup via categoryPath.
      }
    },
    [form],
  );

  // Preserve form data when navigating between steps
  // Restore category dropdown states when navigating back to Basic Info step
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    const steps = getSteps(isAdminMode);
    const currentStepId = steps[currentStep]?.id;
    const prevStepId = steps[prevStepRef.current]?.id;

    // Only restore categories when navigating TO the basic step (not from basic step)
    if (currentStepId === "basic" && prevStepId !== "basic") {
      const categoryId = form.getValues("categoryId");
      if (categoryId && categories.length > 0) {
        findCategoryAndSetStates(categoryId);
      }
    }

    prevStepRef.current = currentStep;
  }, [currentStep, categories, form, isAdminMode, findCategoryAndSetStates]);

  // Track form value changes - REMOVED to prevent interference
  // This watch subscription was potentially causing issues with form state
  // React Hook Form maintains state internally, we don't need to watch every change
  // useEffect(() => {
  //   const subscription = form.watch((value, { name, type }) => {
  //     if (name === "name") {
  //       console.log("Product name changed:", value.name);
  //     }
  //   });
  //   return () => subscription.unsubscribe();
  // }, [form]);

  // ARCHITECTURAL CHANGE: Form is already initialized above with product data
  // Handle media/image state initialization for edit mode
  useEffect(() => {
    // Only set media states when dialog opens and we're in edit mode
    if (!open || !isEdit || !product) return;

    // Set uploaded images from product.images
    if (product.images && product.images.length > 0) {
      const imageItems = product.images.map((url, index) => ({
        id: `existing-${index}`,
        url: url,
        originalName: `Image ${index + 1}`,
      }));
      setUploadedImages(imageItems);
      // Sync with form for validation
      form.setValue(
        "images",
        imageItems.map((item) => item.url),
      );
    }

    // Set thumbnail image
    if (product.thumbnailImage) {
      setThumbnailImage({
        id: `existing-thumbnail`,
        url: product.thumbnailImage,
        originalName: `Thumbnail Image`,
      });
    }

    // Set PDF specification
    if (product.pdfSpecification) {
      setPdfSpecification({
        id: `existing-pdf`,
        url: product.pdfSpecification,
        originalName: `PDF Specification`,
      });
    }

    // Set video URL (supports legacy API key variants)
    const existingVideoUrl = getProductVideoUrl(product).trim();
    setUploadedVideo(existingVideoUrl || null);
    form.setValue("videoUrl", existingVideoUrl, { shouldValidate: false });

    // Set documents
    if (product.documents && product.documents.length > 0) {
      setUploadedDocuments(product.documents);
    }

    // Set certifications
    if (product.certifications && product.certifications.length > 0) {
      setUploadedCertifications(product.certifications);
    }
  }, [open, isEdit, product, form, getProductVideoUrl]);

  // Set cascading dropdown states when both product and categories are available
  useEffect(() => {
    if (isEdit && product && categories.length > 0) {
      // Simple approach: find the category and set the appropriate dropdown states
      const findCategoryAndSetStates = (targetId: string) => {
        // Check if it's a root category
        const rootCategory = categories.find((cat) => cat.id === targetId);
        if (rootCategory) {
          setSelectedParentId(targetId);
          setSelectedChildId("");
          return;
        }

        // Check if it's a level 1 or level 2 category
        for (const rootCat of categories) {
          if (rootCat.children) {
            // Check level 1 categories
            for (const level1Category of rootCat.children) {
              if (level1Category.id === targetId) {
                setSelectedParentId(rootCat.id);
                setSelectedChildId(targetId);
                return;
              }

              // Check level 2 and level 3 categories
              if (level1Category.children) {
                for (const level2Category of level1Category.children) {
                  if (level2Category.id === targetId) {
                    setSelectedParentId(rootCat.id);
                    setSelectedChildId(level1Category.id);
                    return;
                  }
                  if (level2Category.children) {
                    for (const level3Category of level2Category.children) {
                      if (level3Category.id === targetId) {
                        setSelectedParentId(rootCat.id);
                        setSelectedChildId(level1Category.id);
                        return;
                      }
                    }
                  }
                }
              }
            }
          }
        }

        // Fallback: try to find by name
        const foundByCategory = categories.find(
          (cat) => cat.name === product.category,
        );
        if (foundByCategory) {
          setSelectedParentId(foundByCategory.id);
          setSelectedChildId("");
        }
      };

      if (product.categoryId) {
        findCategoryAndSetStates(product.categoryId);
      } else if (product.category) {
        findCategoryAndSetStates(product.category);
      }
    }
  }, [isEdit, product, categories]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newImages = Array.from(files).map((file, index) => ({
        id: `upload-${Date.now()}-${index}`,
        url: URL.createObjectURL(file),
        originalName: file.name,
      }));
      setUploadedImages((prev) => {
        const updated = [...prev, ...newImages];
        // Sync with form for validation
        form.setValue(
          "images",
          updated.map((item) => item.url),
        );
        return updated;
      });
    }
  };

  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedVideo(URL.createObjectURL(file));
    }
  };

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newDocs = Array.from(files).map((file) =>
        URL.createObjectURL(file),
      );
      setUploadedDocuments((prev) => [...prev, ...newDocs]);
    }
  };

  const handleCertificationUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files) {
      const newCerts = Array.from(files).map((file) =>
        URL.createObjectURL(file),
      );
      setUploadedCertifications((prev) => [...prev, ...newCerts]);
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Sync with form for validation
      form.setValue(
        "images",
        updated.map((item) => item.url),
      );
      return updated;
    });
  };

  const addSpecification = () => {
    setSpecifications((prev) => [...prev, { key: "", value: "" }]);
  };

  // const updateSpecification = (
  //   index: number,
  //   field: "key" | "value",
  //   value: string
  // ) => {
  //   setSpecifications((prev) =>
  //     prev.map((spec, i) => (i === index ? { ...spec, [field]: value } : spec))
  //   );
  // };

  // const removeSpecification = (index: number) => {
  //   setSpecifications((prev) => prev.filter((_, i) => i !== index));
  // };

  const addKeyword = (keyword: string) => {
    const currentKeywords = form.getValues("keywords") || [];
    if (keyword && !currentKeywords.includes(keyword)) {
      form.setValue("keywords", [...currentKeywords, keyword]);
      setCurrentKeyword("");
    }
  };

  const removeKeyword = (keywordToRemove: string) => {
    const currentKeywords = form.getValues("keywords") || [];
    form.setValue(
      "keywords",
      currentKeywords.filter((keyword) => keyword !== keywordToRemove),
    );
  };

  const addTieredPrice = () => {
    const current = form.getValues("tieredPricing") || [];
    // Only add if current is empty or if we're adding a new tier
    if (current.length === 0) {
      form.setValue("tieredPricing", [{ quantity: 0, price: 0 }]);
    } else {
      form.setValue("tieredPricing", [...current, { quantity: 0, price: 0 }]);
    }
  };

  const removeTieredPrice = (index: number) => {
    const current = form.getValues("tieredPricing");
    form.setValue(
      "tieredPricing",
      current.filter((_, i) => i !== index),
    );
  };

  const handleTierFieldFocus = (index: number, field: "quantity" | "price") => {
    setActiveTierField({ index, field });
  };

  const handleTierFieldBlur = () => {
    setActiveTierField(null);
  };

  const getTierFieldValue = (
    index: number,
    field: "quantity" | "price",
    value: number | undefined,
  ) => {
    if (
      activeTierField?.index === index &&
      activeTierField?.field === field &&
      value === 0
    ) {
      return "";
    }
    return value ?? "";
  };

  /** Display value for stored unit/tier price (never show NaN; 0 shows empty). */
  const formatStoredPriceForDisplay = (
    raw: number | undefined | null,
  ): string => {
    if (raw === undefined || raw === null) return "";
    if (typeof raw === "number" && Number.isNaN(raw)) return "";
    const n = roundProductPrice(Number(raw));
    if (!Number.isFinite(n)) return "";
    if (n === 0) return "";
    return String(n);
  };

  const getPriceInputDisplay = (
    draftKey: string,
    numeric: number | undefined | null,
  ): string => {
    if (Object.prototype.hasOwnProperty.call(priceInputDrafts, draftKey)) {
      return priceInputDrafts[draftKey];
    }
    return formatStoredPriceForDisplay(numeric);
  };

  const formatStoredMoqForDisplay = (
    numeric: number | undefined | null,
  ): string => {
    if (
      numeric === undefined ||
      numeric === null ||
      !Number.isFinite(numeric)
    ) {
      return "";
    }
    return String(Math.trunc(numeric));
  };

  const getMoqInputDisplay = (numeric: number | undefined | null): string => {
    if (Object.prototype.hasOwnProperty.call(priceInputDrafts, MOQ_DRAFT_KEY)) {
      return priceInputDrafts[MOQ_DRAFT_KEY];
    }
    return formatStoredMoqForDisplay(numeric);
  };

  const setPriceDraft = (draftKey: string, sanitized: string) => {
    setPriceInputDrafts((prev) => ({ ...prev, [draftKey]: sanitized }));
  };

  const clearPriceDraft = (draftKey: string) => {
    setPriceInputDrafts((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, draftKey)) return prev;
      const next = { ...prev };
      delete next[draftKey];
      return next;
    });
  };

  const togglePaymentTerm = (term: string) => {
    const current = form.getValues("paymentTerms");
    const updated = current.includes(term)
      ? current.filter((t) => t !== term)
      : [...current, term];
    form.setValue("paymentTerms", updated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const toggleLogisticsMode = (mode: string) => {
    const current = form.getValues("logisticsModes");
    const updated = current.includes(mode)
      ? current.filter((m) => m !== mode)
      : [...current, mode];
    form.setValue("logisticsModes", updated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const toggleExportMarket = (market: string) => {
    const current = form.getValues("exportMarkets");
    const updated = current.includes(market)
      ? current.filter((m) => m !== market)
      : [...current, market];
    form.setValue("exportMarkets", updated);
  };

  /** When false (default), only compute step completion without form.trigger — avoids showing field errors on load. When true (Next step / save draft), run resolver + manual rules so FormMessage errors appear. */
  type IsStepValidOptions = {
    applyErrorsToForm?: boolean;
    /** Save draft: review step has no inputs — do not cascade validation into other tabs. */
    forDraftSaveOnCurrentStep?: boolean;
  };

  // Comprehensive step validation function
  const isStepValid = useCallback(
    async (
      stepId: string,
      options: IsStepValidOptions = {},
    ): Promise<boolean> => {
      const applyErrorsToForm = options.applyErrorsToForm ?? false;
      const formValues = form.getValues();

      switch (stepId) {
        case "basic": {
          // Required: name and categoryId
          const hasName = formValues.name && formValues.name.trim().length > 0;

          // Final categoryId must be set (either root or parent category)
          const hasCategoryId =
            formValues.categoryId && formValues.categoryId.trim().length > 0;

          // Validate category depth using new validation utility
          // CategoryMegaMenuPopupSelector ensures proper depth, but we validate here too
          let categoryValid = false;

          if (hasCategoryId) {
            // Use new depth validation utility
            // Only validate if categories are loaded
            if (areCategoriesLoaded) {
              // When categories were loaded but the allowed list is empty,
              // treat any category selection as invalid.
              categoryValid =
                categories && categories.length > 0
                  ? isValidCategoryDepth(categories, formValues.categoryId)
                  : false;
            } else {
              // If categories aren't loaded yet, assume valid if categoryId exists
              // This prevents validation failure during initial load.
              categoryValid = true;
            }
          } else {
            // If categoryId is not set, check dropdown-based selection (backward compatibility)
            // Check if a root category is selected via dropdown
            const hasRootCategory =
              selectedParentId && selectedParentId.trim().length > 0;

            if (hasRootCategory) {
              const level1Children = getLevel1Children(selectedParentId);
              if (level1Children.length > 0) {
                // Parent has children - parent category selection is required
                const hasParentCategory = Boolean(
                  selectedChildId && selectedChildId.trim().length > 0,
                );
                categoryValid = hasParentCategory;
              } else {
                // Parent has no children, but Level-1 only is not valid
                // Must select at least Level-2
                categoryValid = false;
              }
            }
          }

          // Required: description (mandatory for create product)
          const hasDescription =
            formValues.description != null &&
            String(formValues.description).trim().length > 0;

          // Validate schema fields: name, categoryId, description (match zod — categoryId is required at field level)
          const basicFields: (keyof ProductFormData)[] = [
            "name",
            "categoryId",
            "description",
          ];
          let schemaValid: boolean;
          if (applyErrorsToForm) {
            schemaValid = await form.trigger(basicFields, {
              shouldFocus: true,
            });
          } else {
            schemaValid = Boolean(
              hasName &&
                hasDescription &&
                formValues.categoryId &&
                formValues.categoryId.trim().length > 0,
            );
          }

          const result = Boolean(
            hasName && hasDescription && categoryValid && schemaValid,
          );

          // Debug logging for basic step validation
          if (!result) {
            console.log("Basic step validation failed:", {
              hasName,
              hasCategoryId,
              categoryValid,
              schemaValid,
              categoryId: formValues.categoryId,
              categoriesLength: categories?.length || 0,
              formErrors: form.formState.errors,
            });
          }

          return result;
        }

        case "pricing": {
          // Required: unit price, MOQ, and at least one payment term
          const hasPrice =
            formValues.price !== undefined &&
            formValues.price !== null &&
            Number(formValues.price) > 0;

          const hasMoq =
            formValues.moq !== undefined &&
            formValues.moq !== null &&
            Number.isFinite(Number(formValues.moq)) &&
            Number(formValues.moq) >= 1;

          const hasPaymentTerms =
            Array.isArray(formValues.paymentTerms) &&
            formValues.paymentTerms.length > 0;

          const pricingFields: (keyof ProductFormData)[] = ["price", "moq"];
          let schemaValid: boolean;
          if (applyErrorsToForm) {
            if (hasPrice) {
              form.clearErrors("price");
            }
            if (hasMoq) {
              form.clearErrors("moq");
            }
            schemaValid = await form.trigger(pricingFields, {
              shouldFocus: true,
            });
            // Zod allows price === 0; step requires a positive unit price for this tab.
            if (!hasPrice) {
              form.setError("price", {
                type: "manual",
                message: "Unit price must be greater than 0",
              });
            }
            if (!hasMoq) {
              form.setError("moq", {
                type: "manual",
                message: "Minimum order quantity is required",
              });
            }
            if (hasPaymentTerms) {
              form.clearErrors("paymentTerms");
            }
            if (!hasPaymentTerms) {
              form.setError("paymentTerms", {
                type: "manual",
                message: "Select at least one payment term",
              });
            }
          } else {
            schemaValid = Boolean(
              typeof formValues.price === "number" &&
                !Number.isNaN(formValues.price) &&
                formValues.price >= 0 &&
                hasMoq,
            );
          }

          return Boolean(hasPrice && hasMoq && hasPaymentTerms && schemaValid);
        }

        case "logistics": {
          // Required fields marked with * in UI: shippingPort, packaging
          const hasShippingPort =
            formValues.shippingPort &&
            formValues.shippingPort.trim().length > 0;
          const hasPackaging =
            formValues.packaging && formValues.packaging.trim().length > 0;

          // Validate schema fields (only fields marked with *)
          const logisticsFields: (keyof ProductFormData)[] = [
            "shippingPort",
            "packaging",
          ];
          let schemaValid: boolean;
          if (applyErrorsToForm) {
            if (hasShippingPort) {
              form.clearErrors("shippingPort");
            }
            if (hasPackaging) {
              form.clearErrors("packaging");
            }
            schemaValid = await form.trigger(logisticsFields, {
              shouldFocus: true,
            });
            // Schema uses default("") for these; enforce required on this step.
            if (!hasShippingPort) {
              form.setError("shippingPort", {
                type: "manual",
                message: "Shipping port is required",
              });
            }
            if (!hasPackaging) {
              form.setError("packaging", {
                type: "manual",
                message: "Packing details are required",
              });
            }
          } else {
            schemaValid = true;
          }

          return Boolean(hasShippingPort && hasPackaging && schemaValid);
        }

        case "media": {
          // Required: gallery images (at least 1, max 5). Thumbnail optional (first gallery image used if not provided).
          const hasGalleryImages = uploadedImages && uploadedImages.length > 0;

          // Check form images field - sync from uploadedImages if needed
          const formImages = formValues.images || [];
          const hasFormImages =
            Array.isArray(formImages) && formImages.length > 0;

          const mediaFields: (keyof ProductFormData)[] = ["images"];
          let schemaValid: boolean;

          if (applyErrorsToForm) {
            if (hasGalleryImages && !hasFormImages) {
              const imageUrls = uploadedImages.map((item) => item.url);
              form.setValue("images", imageUrls, { shouldValidate: true });
              await form.trigger("images", { shouldFocus: false });
            }

            if (hasGalleryImages) {
              const imageUrls = uploadedImages.map((item) => item.url);
              form.setValue("images", imageUrls, { shouldValidate: true });
              schemaValid = await form.trigger(mediaFields, {
                shouldFocus: true,
              });
            } else {
              schemaValid = await form.trigger(mediaFields, {
                shouldFocus: true,
              });
            }
          } else {
            if (hasGalleryImages && !hasFormImages) {
              const imageUrls = uploadedImages.map((item) => item.url);
              form.setValue("images", imageUrls, { shouldValidate: false });
            }
            const imgs = form.getValues("images") || [];
            schemaValid =
              Array.isArray(imgs) && imgs.length >= 1 && imgs.length <= 5;
          }

          return Boolean(hasGalleryImages && schemaValid);
        }

        case "review": {
          if (options.forDraftSaveOnCurrentStep) {
            return true;
          }
          // Re-validate all previous steps
          const basicValid = await isStepValid("basic", options);
          const pricingValid = await isStepValid("pricing", options);
          const logisticsValid = await isStepValid("logistics", options);
          const mediaValid = await isStepValid("media", options);

          return Boolean(
            basicValid && pricingValid && logisticsValid && mediaValid,
          );
        }

        case "seller-assignment": {
          // For admin mode, seller assignment step validation:
          // - If assignment type is "admin", always valid
          // - If assignment type is "seller", require selectedSellerId
          if (!isAdminMode) {
            return true; // Not admin mode, skip this step validation
          }

          // If user has explicitly chosen "admin", it's always valid
          if (assignmentType === "admin") {
            return true;
          }

          // If user has chosen "seller", they must select a seller
          if (assignmentType === "seller") {
            const hasSeller = Boolean(
              selectedSellerId && selectedSellerId.trim().length > 0,
            );
            console.log("Seller assignment validation:", {
              assignmentType,
              selectedSellerId,
              hasSeller,
            });
            return hasSeller;
          }

          // Default: allow proceeding (assignmentType might not be initialized yet)
          return true;
        }

        default:
          return true;
      }
    },
    [
      form,
      categories,
      areCategoriesLoaded,
      selectedParentId,
      selectedChildId,
      uploadedImages,
      thumbnailImage,
      isAdminMode,
      assignmentType,
      selectedSellerId,
      selectedSeller,
    ],
  );

  const nextStep = useCallback(async () => {
    if (wizardLocked) {
      return;
    }
    if (currentStep < getSteps(isAdminMode).length - 1) {
      const steps = getSteps(isAdminMode);
      const currentStepId = steps[currentStep]?.id;

      // Use comprehensive step validation (surface field errors only when user tries to advance)
      const isValid = await isStepValid(currentStepId, {
        applyErrorsToForm: true,
      });
      if (!isValid) {
        console.log("Validation failed for step:", currentStepId);
        console.log("Form errors:", form.formState.errors);
        // For seller-assignment step, log additional debug info
        if (currentStepId === "seller-assignment") {
          console.log("Assignment type:", assignmentType);
          console.log("Selected seller ID:", selectedSellerId);
          console.log("Selected seller:", selectedSeller);
        }
        return; // Don't proceed if validation fails
      }

      // Mark current step as validated
      setValidatedSteps((prev) => new Set([...prev, currentStepId]));

      setCurrentStep(currentStep + 1);
    }
  }, [
    currentStep,
    isAdminMode,
    isStepValid,
    form,
    assignmentType,
    selectedSellerId,
    selectedSeller,
    wizardLocked,
  ]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Full-page create/edit: reset ALL scroll containers on step change (footer, stepper, async next).
  // Layout: AppLayout <main overflow-y-auto> → nested form column with its own overflow-y-auto.
  const skipStepScrollOnMountRef = useRef(true);
  useEffect(() => {
    if (!renderAsPage) return;
    if (skipStepScrollOnMountRef.current) {
      skipStepScrollOnMountRef.current = false;
      return;
    }
    const scrollPageToStepStart = () => {
      const inner = pageFormScrollRef.current;
      if (inner) {
        inner.scrollTop = 0;
        inner.scrollLeft = 0;
      }
      const headerEl = pageStepHeaderRef.current;
      if (headerEl) {
        headerEl.scrollIntoView({
          block: "start",
          inline: "nearest",
          behavior: "auto",
        });
      }
      const main = document.querySelector("main");
      if (main instanceof HTMLElement) {
        main.scrollTop = 0;
        main.scrollLeft = 0;
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    };
    // Double rAF: run after React commit + browser layout (step content height may change).
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollPageToStepStart);
    });
  }, [currentStep, renderAsPage]);

  const onSubmit = (data: ProductFormData) => {
    const resolvedVideoUrl =
      (uploadedVideo ?? data.videoUrl ?? "").trim() || undefined;
    const payloadData = withMetaKeywordsPayload(data);
    // Transform form data to match API structure
    const transformedData = applySeoFieldsToPayload({
      ...payloadData,
      price: roundProductPrice(Number(data.price ?? 0)),
      // Use description field
      description: data.description || "",
      // Map images from uploadedImages state
      images: uploadedImages.map((item) => item.url) || [],
      // Map new media fields (thumbnail: use first gallery image if not provided)
      thumbnailImage:
        thumbnailImage?.url || uploadedImages?.[0]?.url || undefined,
      pdfSpecification: pdfSpecification?.url || undefined,
      videoUrl: resolvedVideoUrl,
      documents: uploadedDocuments || [],
      certifications: uploadedCertifications || [],
      // Map tags from keywords if tags is empty
      tags: data.tags && data.tags.length > 0 ? data.tags : [],
      paymentTerms: data.paymentTerms || [],
      moq: data.moq,
      moqUnit: data.unitOfMeasure || data.moqUnit || "pieces",
      logisticsModes: data.logisticsModes || [],
      exportMarkets: data.exportMarkets || [],
      tieredPricing: serializeTieredPricingForPayload(data.tieredPricing),
      // Price fields - only send if > 0 (backend requires positive numbers)
      priceMin: data.priceMin && data.priceMin > 0 ? data.priceMin : undefined,
      priceMax: data.priceMax && data.priceMax > 0 ? data.priceMax : undefined,
      // Include new fields
      discount: data.discount,
      discountStartDate: data.discountStartDate,
      discountEndDate: data.discountEndDate,
      discountQuantity: data.discountQuantity,
    });

    // Handle assignment based on admin mode and assignment type
    let productData = transformedData;

    if (isAdminMode) {
      if (assignmentType === "seller" && selectedSellerId) {
        // Assign to specific seller
        productData = {
          ...transformedData,
          assignedSellerId: selectedSellerId,
        };
      } else if (assignmentType === "admin") {
        // Admin owned - no assignedSellerId needed
        productData = { ...transformedData, assignedSellerId: undefined };
      }
    }

    if (isEdit && onUpdate) {
      onUpdate(productData);
    } else if (onSave) {
      onSave(productData);
    }
    setOpen(false);
  };

  // Watch required fields for publish button validation and step validation
  const watchedName = form.watch("name");
  const watchedUnitOfMeasure = form.watch("unitOfMeasure");
  const watchedPrice = form.watch("price");
  const watchedPaymentTerms = form.watch("paymentTerms");
  // Convert array to string for dependency tracking (arrays don't trigger re-renders well)
  const watchedPaymentTermsString = JSON.stringify(watchedPaymentTerms || []);
  const watchedShippingPort = form.watch("shippingPort");
  const watchedPackaging = form.watch("packaging");
  const watchedThumbnailImage = form.watch("thumbnailImage");
  const watchedDiscount = form.watch("discount");
  const watchedMoq = form.watch("moq");
  const watchedCategory = form.watch("category");
  const watchedCategoryId = form.watch("categoryId");
  const watchedDescription = form.watch("description");
  const watchedCompanyId = form.watch("companyId");
  const watchedSku = form.watch("sku");
  const watchedPriceMin = form.watch("priceMin");
  const watchedPriceMax = form.watch("priceMax");
  const watchedDiscountQuantity = form.watch("discountQuantity");
  // Note: deliveryTime and logisticsModes are not marked with * in UI, so not watched for step validation
  // Note: watchedDiscount is already declared above for publish validation

  // Check if current step is valid - watch form values to trigger re-validation
  useEffect(() => {
    const checkStepValidity = async () => {
      const steps = getSteps(isAdminMode);
      const currentStepId = steps[currentStep]?.id;
      const isValid = await isStepValid(currentStepId, {
        applyErrorsToForm: false,
      });
      setCurrentStepValid(isValid);
    };

    // Small delay to allow form state to update after user input
    const timeoutId = setTimeout(() => {
      checkStepValidity();
    }, 100);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    thumbnailImage,
    uploadedImages, // Add uploadedImages to trigger validation when gallery images change
    selectedSellerId,
    assignmentType,
    isAdminMode,
    selectedSeller, // Add selectedSeller to trigger validation when seller changes
    // Watch all relevant form fields for validation
    watchedName,
    watchedCategory,
    watchedCategoryId,
    watchedDescription, // For basic step: hide Next when empty
    selectedParentId, // For checking if root category is selected
    selectedChildId, // For checking if parent category is selected (when required)
    // DEPRECATED: watchedSku,
    watchedPrice, // Required for pricing step validation
    // DEPRECATED: watchedCompanyId,
    // DEPRECATED: watchedUnitOfMeasure,
    watchedPriceMin,
    watchedPriceMax,
    watchedMoq,
    watchedPaymentTermsString, // Use stringified version for proper dependency tracking
    watchedDiscount,
    watchedDiscountQuantity,
    watchedShippingPort,
    watchedPackaging,
    // Note: deliveryTime and logisticsModes are not marked with * in UI, so not validated for step progression
    watchedThumbnailImage,
  ]);

  // Check if all required fields for publish are valid
  const isPublishEnabled = useMemo(() => {
    // 1. Product Name * (name)
    const hasName = watchedName && watchedName.trim().length > 0;

    // 2. Unit (unitOfMeasure) - DEPRECATED: Now optional
    // const hasUnit = watchedUnitOfMeasure && watchedUnitOfMeasure.trim().length > 0;

    // 3. Unit price * (price)
    const hasPrice =
      watchedPrice !== undefined &&
      watchedPrice !== null &&
      Number(watchedPrice) > 0;

    // 4. Price Term * (paymentTerms - at least one item)
    const hasPaymentTerms =
      watchedPaymentTerms &&
      Array.isArray(watchedPaymentTerms) &&
      watchedPaymentTerms.length > 0;

    // 5. Payment method * (same as paymentTerms - at least one item)
    // Using paymentTerms for both Price Term and Payment method
    const hasPaymentMethod = hasPaymentTerms;

    // 6. Shipping Port * (shippingPort)
    const hasShippingPort =
      watchedShippingPort && watchedShippingPort.trim().length > 0;

    // 7. Packing Type * (packaging)
    const hasPackaging = watchedPackaging && watchedPackaging.trim().length > 0;

    // 8. Gallery Images * (uploadedImages - at least 1 required, max 5)
    const hasGalleryImages = uploadedImages && uploadedImages.length > 0;

    // 9. Thumbnail Image - optional; first gallery image used if not provided

    // 10. Minimum order quantity (MOQ) *
    const hasMoq =
      watchedMoq !== undefined &&
      watchedMoq !== null &&
      Number.isFinite(Number(watchedMoq)) &&
      Number(watchedMoq) >= 1;

    // Category (either category or categoryId must be present) - keeping this for backward compatibility
    const hasCategory =
      (watchedCategory && watchedCategory.trim().length > 0) ||
      (watchedCategoryId && watchedCategoryId.trim().length > 0);

    // Company ID (required for API) - use prop if form value is empty
    const formCompanyId = toTrimmedString(watchedCompanyId || companyId);
    const hasCompanyId = formCompanyId.length > 0;

    // All fields must be valid (thumbnail optional; first gallery image used if not provided)
    const isValid = !!(
      hasName &&
      hasPrice &&
      hasMoq &&
      hasPaymentTerms &&
      hasPaymentMethod &&
      hasShippingPort &&
      hasPackaging &&
      hasGalleryImages &&
      hasCategory &&
      hasCompanyId
    );

    // Debug logging when validation fails
    if (!isValid) {
      console.log("Publish validation failed - missing fields:", {
        hasName,
        hasPrice,
        hasMoq,
        hasPaymentTerms,
        hasPaymentMethod,
        hasShippingPort,
        hasPackaging,
        hasGalleryImages,
        hasCategory,
        hasCompanyId,
        watchedName,
        watchedPrice,
        watchedPaymentTerms,
        watchedShippingPort,
        watchedPackaging,
        uploadedImagesCount: uploadedImages?.length || 0,
        watchedCategory,
        watchedCategoryId,
        watchedCompanyId,
        companyIdProp: companyId,
      });
    }

    return isValid;
  }, [
    watchedName,
    // DEPRECATED: watchedUnitOfMeasure,
    watchedPrice,
    watchedPaymentTerms,
    watchedShippingPort,
    watchedPackaging,
    uploadedImages, // Add uploadedImages to trigger re-validation when gallery images change
    watchedThumbnailImage,
    // DEPRECATED: watchedDiscount,
    watchedMoq,
    watchedCategory,
    watchedCategoryId,
    watchedCompanyId,
    thumbnailImage,
    companyId, // Add companyId prop to dependencies
  ]);

  // Ensure companyId is always set in the form when prop changes
  useEffect(() => {
    const safeCompanyId = toTrimmedString(companyId);
    if (safeCompanyId.length > 0) {
      const currentCompanyId = toTrimmedString(form.getValues("companyId"));
      if (currentCompanyId.length === 0) {
        form.setValue("companyId", safeCompanyId, { shouldValidate: false });
      }
    }
  }, [companyId, form]);

  // Sync thumbnail image with form whenever it changes
  useEffect(() => {
    if (thumbnailImage && thumbnailImage.url) {
      form.setValue("thumbnailImage", thumbnailImage.url, {
        shouldValidate: false,
      });
    } else if (!thumbnailImage) {
      // Only clear if form value exists (don't overwrite if user manually set it)
      const currentThumbnail = form.getValues("thumbnailImage");
      if (currentThumbnail) {
        form.setValue("thumbnailImage", "", { shouldValidate: false });
      }
    }
  }, [thumbnailImage, form]);

  const saveAsDraft = async () => {
    if (wizardLocked) {
      return;
    }

    const steps = getSteps(isAdminMode);
    const currentStepId = steps[currentStep]?.id ?? "basic";

    // Save draft: validate only this step's required fields, then POST. Review has no inputs (no extra checks).
    // Drop errors from other steps only — full clearErrors() prevents current-step messages from showing after trigger.
    const fieldsToClear = DRAFT_SAVE_CLEAR_ERRORS[currentStepId];
    if (fieldsToClear?.length) {
      form.clearErrors(fieldsToClear);
    }

    const isValid = await isStepValid(currentStepId, {
      applyErrorsToForm: true,
      forDraftSaveOnCurrentStep: true,
    });

    if (!isValid) {
      const errors = form.formState.errors;

      const scrollFieldOrder: Record<string, string[]> = {
        "seller-assignment": [],
        basic: ["name", "categoryId", "description"],
        pricing: ["price", "moq", "paymentTerms"],
        logistics: ["shippingPort", "packaging"],
        media: ["images"],
        review: [],
      };

      const tryScrollToField = (field: string) => {
        const fieldElement =
          document.querySelector(`[name="${field}"]`) ||
          document.querySelector(`[id="${field}"]`);
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          (fieldElement as HTMLElement)?.focus();
          return true;
        }
        return false;
      };

      let scrolledToField = false;
      for (const field of scrollFieldOrder[currentStepId] ?? []) {
        if (errors[field as keyof typeof errors]) {
          if (tryScrollToField(field)) {
            scrolledToField = true;
            return;
          }
        }
      }

      if (currentStepId === "basic" && !scrolledToField) {
        const categoryElement =
          document.querySelector(`[name="category"]`) ||
          document.querySelector(`[id="category"]`) ||
          document.querySelector(`[name="categoryId"]`) ||
          document.querySelector(`[id="categoryId"]`);
        if (categoryElement) {
          categoryElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          (categoryElement as HTMLElement)?.focus();
        }
      }

      if (
        currentStepId === "seller-assignment" &&
        assignmentType === "seller"
      ) {
        const sellerSection = document.querySelector(
          "[data-seller-assignment-select]",
        );
        if (sellerSection) {
          sellerSection.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      return;
    }

    const data = withMetaKeywordsPayload(form.getValues());
    const fallbackCompanyId = toTrimmedString(data.companyId || companyId);
    if (
      fallbackCompanyId.length > 0 &&
      toTrimmedString(data.companyId).length === 0
    ) {
      form.setValue("companyId", fallbackCompanyId, { shouldValidate: false });
      data.companyId = fallbackCompanyId;
    }
    data.status = "draft";
    const resolvedVideoUrl =
      (uploadedVideo ?? data.videoUrl ?? "").trim() || undefined;

    // Transform data before calling onSaveDraft
    const transformedDataBase = applySeoFieldsToPayload({
      ...data,
      price: roundProductPrice(Number(data.price ?? 0)),
      description: data.description || "",
      images: uploadedImages.map((item) => item.url) || [],
      // Map new media fields (thumbnail: use first gallery image if not provided)
      thumbnailImage:
        thumbnailImage?.url || uploadedImages?.[0]?.url || undefined,
      pdfSpecification: pdfSpecification?.url || undefined,
      videoUrl: resolvedVideoUrl,
      documents: uploadedDocuments || [],
      certifications: uploadedCertifications || [],
      tags: data.tags && data.tags.length > 0 ? data.tags : [],
      paymentTerms: data.paymentTerms || [],
      logisticsModes: data.logisticsModes || [],
      exportMarkets: data.exportMarkets || [],
      tieredPricing: serializeTieredPricingForPayload(data.tieredPricing),
      // Price fields - only send if > 0 (backend requires positive numbers)
      priceMin: data.priceMin && data.priceMin > 0 ? data.priceMin : undefined,
      priceMax: data.priceMax && data.priceMax > 0 ? data.priceMax : undefined,
      // Include new fields
      discount: data.discount,
      discountStartDate: data.discountStartDate,
      discountEndDate: data.discountEndDate,
      discountQuantity: data.discountQuantity,
    });

    // Keep admin assignment payload behavior aligned with main submit flow.
    const transformedData =
      isAdminMode && assignmentType === "seller" && selectedSellerId
        ? { ...transformedDataBase, assignedSellerId: selectedSellerId }
        : isAdminMode && assignmentType === "admin"
          ? { ...transformedDataBase, assignedSellerId: undefined }
          : transformedDataBase;

    try {
      setInternalLoading(true);
      if (onSaveDraft) {
        await Promise.resolve(onSaveDraft(transformedData));
      } else {
        onSubmit(transformedData);
      }
    } catch (error) {
      console.error("Error saving as draft:", error);
      // Re-throw to let parent handle it
      throw error;
    } finally {
      setInternalLoading(false);
    }
  };

  const publishProduct = async () => {
    if (wizardLocked) {
      return;
    }
    console.log("Publish Product clicked");

    const currentValues = form.getValues();
    const fallbackCompanyId = toTrimmedString(
      currentValues.companyId || companyId,
    );
    if (
      fallbackCompanyId.length > 0 &&
      toTrimmedString(currentValues.companyId).length === 0
    ) {
      form.setValue("companyId", fallbackCompanyId, { shouldValidate: true });
    }

    // Validate all required fields for publish
    const publishRequiredFields: (keyof ProductFormData)[] = [
      "name",
      "price",
      "moq",
      "companyId",
    ];

    // Trigger validation for required fields - trigger accepts array of field names
    const validationResults = await Promise.all(
      publishRequiredFields.map((field) => form.trigger(field)),
    );

    // Get form values for additional validation
    const formValues = form.getValues();

    // Validate category (either category or categoryId must be present)
    const hasCategory =
      (formValues.category && formValues.category.trim().length > 0) ||
      (formValues.categoryId && formValues.categoryId.trim().length > 0);

    // Validate paymentTerms (at least one item required)
    const hasPaymentTerms =
      formValues.paymentTerms &&
      Array.isArray(formValues.paymentTerms) &&
      formValues.paymentTerms.length > 0;

    const hasMoq =
      formValues.moq !== undefined &&
      formValues.moq !== null &&
      Number.isFinite(Number(formValues.moq)) &&
      Number(formValues.moq) >= 1;

    // Validate shippingPort
    const hasShippingPort =
      formValues.shippingPort && formValues.shippingPort.trim().length > 0;

    // Validate packaging
    const hasPackaging =
      formValues.packaging && formValues.packaging.trim().length > 0;

    // Validate gallery images (at least 1 required, max 5). Thumbnail optional.
    const hasGalleryImages = uploadedImages && uploadedImages.length > 0;

    // DEPRECATED: Validate discount - no longer required
    // const hasDiscount =
    //   formValues.discount !== undefined &&
    //   formValues.discount !== null &&
    //   Number(formValues.discount) >= 0;

    const allValidations = [
      ...validationResults,
      hasCategory,
      hasMoq,
      hasPaymentTerms,
      hasShippingPort,
      hasPackaging,
      hasGalleryImages,
    ];
    const isValid = allValidations.every((result) => result === true);
    console.log(
      "Validation results:",
      validationResults,
      "hasCategory:",
      hasCategory,
      "hasPaymentTerms:",
      hasPaymentTerms,
      "hasShippingPort:",
      hasShippingPort,
      "hasPackaging:",
      hasPackaging,
      "hasGalleryImages:",
      hasGalleryImages,
      "isValid:",
      isValid,
    );

    if (!isValid) {
      // Get form errors to show user
      const errors = form.formState.errors;
      console.log("Validation errors:", errors);

      // Check fields in order and scroll to first invalid one
      if (!hasCategory) {
        const categoryElement =
          document.querySelector(`[name="category"]`) ||
          document.querySelector(`[id="category"]`) ||
          document.querySelector(`[name="categoryId"]`) ||
          document.querySelector(`[id="categoryId"]`);
        if (categoryElement) {
          categoryElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          (categoryElement as HTMLElement)?.focus();
        }
        return;
      }

      if (!hasPaymentTerms) {
        const paymentTermsElement =
          document.querySelector(`[name="paymentTerms"]`) ||
          document.querySelector(`[id="paymentTerms"]`) ||
          document.querySelector(`[data-field="paymentTerms"]`);
        if (paymentTermsElement) {
          paymentTermsElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          (paymentTermsElement as HTMLElement)?.focus();
        }
        return;
      }

      if (!hasShippingPort) {
        const shippingPortElement =
          document.querySelector(`[name="shippingPort"]`) ||
          document.querySelector(`[id="shippingPort"]`);
        if (shippingPortElement) {
          shippingPortElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          (shippingPortElement as HTMLElement)?.focus();
        }
        return;
      }

      if (!hasPackaging) {
        const packagingElement =
          document.querySelector(`[name="packaging"]`) ||
          document.querySelector(`[id="packaging"]`);
        if (packagingElement) {
          packagingElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          (packagingElement as HTMLElement)?.focus();
        }
        return;
      }

      if (!hasGalleryImages) {
        const galleryElement =
          document.querySelector(`[data-gallery-images]`) ||
          document.querySelector(`button:has-text("Choose File")`);
        if (galleryElement) {
          galleryElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          (galleryElement as HTMLElement)?.focus();
        }
        return;
      }

      // DEPRECATED: Discount validation removed - no longer required
      // if (!hasDiscount) { ... }

      // Find the first invalid field from form validation and scroll to it
      const invalidFieldIndex = validationResults.findIndex(
        (result) => !result,
      );
      const invalidField =
        invalidFieldIndex >= 0
          ? publishRequiredFields[invalidFieldIndex]
          : null;

      if (invalidField) {
        // Try to find the field element and scroll to it
        const fieldElement =
          document.querySelector(`[name="${invalidField}"]`) ||
          document.querySelector(`[id="${invalidField}"]`);
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
          // Focus the field
          (fieldElement as HTMLElement)?.focus();
        }
      }
      return; // Don't proceed if validation fails
    }

    const data = withMetaKeywordsPayload(form.getValues());
    data.status = "active";
    const resolvedVideoUrl =
      (uploadedVideo ?? data.videoUrl ?? "").trim() || undefined;

    // Transform data before calling onPublish
    const transformedDataBase = applySeoFieldsToPayload({
      ...data,
      price: roundProductPrice(Number(data.price ?? 0)),
      description: data.description || "",
      images: uploadedImages.map((item) => item.url) || [],
      // Map new media fields (thumbnail: use first gallery image if not provided)
      thumbnailImage:
        thumbnailImage?.url || uploadedImages?.[0]?.url || undefined,
      pdfSpecification: pdfSpecification?.url || undefined,
      videoUrl: resolvedVideoUrl,
      documents: uploadedDocuments || [],
      certifications: uploadedCertifications || [],
      tags: data.tags && data.tags.length > 0 ? data.tags : [],
      paymentTerms: data.paymentTerms || [],
      logisticsModes: data.logisticsModes || [],
      exportMarkets: data.exportMarkets || [],
      tieredPricing: serializeTieredPricingForPayload(data.tieredPricing),
      // Price fields - only send if > 0 (backend requires positive numbers)
      priceMin: data.priceMin && data.priceMin > 0 ? data.priceMin : undefined,
      priceMax: data.priceMax && data.priceMax > 0 ? data.priceMax : undefined,
      // Include new fields
      discount: data.discount,
      discountStartDate: data.discountStartDate,
      discountEndDate: data.discountEndDate,
      discountQuantity: data.discountQuantity,
    });

    // Keep admin assignment payload behavior aligned with main submit flow.
    const transformedData =
      isAdminMode && assignmentType === "seller" && selectedSellerId
        ? { ...transformedDataBase, assignedSellerId: selectedSellerId }
        : isAdminMode && assignmentType === "admin"
          ? { ...transformedDataBase, assignedSellerId: undefined }
          : transformedDataBase;

    try {
      setInternalLoading(true);
      if (onPublish) {
        await Promise.resolve(onPublish(transformedData));
      } else {
        onSubmit(transformedData);
      }
    } catch (error) {
      console.error("Error publishing product:", error);
      // Re-throw to let parent handle it
      throw error;
    } finally {
      setInternalLoading(false);
    }
  };

  // Notify parent of footer state changes when renderAsPage is true
  useEffect(() => {
    if (renderAsPage && onFooterStateChange) {
      const steps = getSteps(isAdminMode);
      const currentStepId = steps[currentStep]?.id;
      const hasDescription =
        watchedDescription != null &&
        String(watchedDescription).trim().length > 0;
      const showNextButton =
        !wizardLocked &&
        currentStep < steps.length - 1 &&
        (currentStepId !== "basic" || hasDescription);

      onFooterStateChange({
        currentStep,
        canGoNext:
          !wizardLocked && currentStep < steps.length - 1 && currentStepValid,
        canGoPrev: currentStep > 0,
        isPublishEnabled: !wizardLocked && isPublishEnabled,
        totalSteps: steps.length,
        showNextButton,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    currentStepValid,
    isPublishEnabled,
    renderAsPage,
    isAdminMode,
    watchedDescription,
    wizardLocked,
  ]);

  // Expose handlers to parent when renderAsPage is true
  useEffect(() => {
    if (renderAsPage && footerHandlers) {
      footerHandlers.onPrev = prevStep;
      footerHandlers.onNext = nextStep;
      footerHandlers.onSaveDraft = saveAsDraft;
      footerHandlers.onPublish = publishProduct;
      if (onArchive) {
        footerHandlers.onArchive = () => {
          onArchive();
          if (onOpenChange) onOpenChange(false);
        };
      }
    }
  }, [
    renderAsPage,
    footerHandlers,
    prevStep,
    nextStep,
    saveAsDraft,
    publishProduct,
    onArchive,
    onOpenChange,
  ]);

  const renderStepContent = () => {
    const steps = getSteps(isAdminMode);
    const currentStepId = steps[currentStep]?.id;

    switch (currentStepId) {
      case "seller-assignment":
        return (
          <div className="space-y-6">
            <div className="text-center py-8">
              <User className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Product Assignment</h3>
              <p className="text-gray-600 mb-6">
                Choose who this product will be assigned to
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-6">
              {/* Assignment Type Selection */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Assignment Type
                  <span className="text-red-500"> *</span>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setAssignmentType("seller")}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      assignmentType === "seller"
                        ? "border-orange-500 bg-orange-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-4 h-4 rounded-full border-2 ${
                          assignmentType === "seller"
                            ? "border-orange-500 bg-orange-500"
                            : "border-gray-300"
                        }`}
                      >
                        {assignmentType === "seller" && (
                          <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">Assign to Seller</div>
                        <div className="text-sm text-gray-500">
                          Product will be assigned to a specific seller
                        </div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAssignmentType("admin")}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      assignmentType === "admin"
                        ? "border-orange-500 bg-orange-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-4 h-4 rounded-full border-2 ${
                          assignmentType === "admin"
                            ? "border-orange-500 bg-orange-500"
                            : "border-gray-300"
                        }`}
                      >
                        {assignmentType === "admin" && (
                          <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">Admin Owned</div>
                        <div className="text-sm text-gray-500">
                          Product will be owned by admin
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Seller Selection (only show when assignment type is seller) */}
              {assignmentType === "seller" && (
                <div className="space-y-2" data-seller-assignment-select>
                  <label className="block text-sm font-medium text-gray-700">
                    Select Seller
                    <span className="text-red-500"> *</span>
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => setIsSellerDialogOpen(true)}
                  >
                    <span className="flex items-center gap-2 flex-1 min-w-0">
                      {selectedSeller ? (
                        <>
                          <User className="h-4 w-4 flex-shrink-0" />
                          <span className="text-left flex-1 min-w-0">
                            <span className="font-medium block truncate">
                              {`${selectedSeller.firstName || ""} ${selectedSeller.lastName || ""}`.trim() ||
                                selectedSeller.email}
                            </span>
                            {selectedSeller.company?.name && (
                              <span className="text-sm text-gray-500 block truncate">
                                {selectedSeller.company.name}
                              </span>
                            )}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-500">Choose a seller</span>
                      )}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </Button>
                  {!selectedSeller && (
                    <p className="text-sm text-gray-500 mt-1">
                      Click to search and select a seller
                    </p>
                  )}
                </div>
              )}

              {/* Admin owned info */}
              {assignmentType === "admin" && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-700">
                      This product will be created and owned by the admin
                      account.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case "basic":
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  key={`${formInstanceKey}-name`}
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="!text-gray-900">
                        Product Name <span className="text-red-500">*</span>
                        <span className="text-xs text-gray-500 ml-2 font-normal">
                          ({String(field.value ?? "").length}/{PRODUCT_NAME_MAX_LENGTH}{" "}
                          characters)
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter product name"
                          maxLength={PRODUCT_NAME_MAX_LENGTH}
                          value={(() => {
                            // Type-safe value handling
                            const val = field.value;
                            // Always return string - React Hook Form should maintain string type
                            if (val === null || val === undefined) return "";
                            if (typeof val === "string") return val;
                            // Safety check: if somehow we get non-string, log and return empty
                            console.warn(
                              "Product name field has non-string value:",
                              val,
                              typeof val,
                            );
                            return "";
                          })()}
                          onChange={(e) => {
                            const value = e.target.value.slice(
                              0,
                              PRODUCT_NAME_MAX_LENGTH,
                            );
                            field.onChange(value);
                            form.setValue("metaTitle", value, {
                              shouldValidate: false,
                            });
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          type="text"
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Category Field - Full Width Single Row */}
              <FormField
                key={`${formInstanceKey}-categoryId`}
                control={form.control}
                name="categoryId"
                render={({ field }) => {
                  // Check if parent category is selected but no subcategory
                  const checkSubcategoryRequired = () => {
                    // Use new depth validation utility
                    if (field.value) {
                      return !isValidCategoryDepth(categories, field.value);
                    }
                    return false;
                  };

                  const needsSubcategory = checkSubcategoryRequired();
                  const validationError = needsSubcategory
                    ? "Please select a subcategory"
                    : (form.formState.errors.categoryId?.message as
                        | string
                        | undefined);

                  return (
                    <FormItem className="w-full">
                      {isCategoryRestrictionEnabled &&
                      areCategoriesLoaded &&
                      categories.length === 0 ? (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm font-medium text-yellow-800">
                            No allowed categories for your membership plan
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Update your membership industries in your business
                            profile to create products.
                          </p>
                        </div>
                      ) : (
                        <CategoryMegaMenuPopupSelector
                          categories={categories}
                          selectedCategoryId={field.value || ""}
                          onSelect={(categoryId, categoryPath) => {
                            handleCategorySelect(categoryId, categoryPath);
                            field.onChange(categoryId);
                          }}
                          placeholder="Select category"
                          label="Category"
                          required={true}
                          error={validationError}
                          industrySelectionMode={isCategoryRestrictionEnabled}
                        />
                      )}
                    </FormItem>
                  );
                }}
              />

              {/* DEPRECATED: HS Code field removed */}

              <FormField
                key={`${formInstanceKey}-brand`}
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Brand</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter brand name"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* DEPRECATED: Model field removed */}

              {/* DEPRECATED: SKU field removed */}
            </div>

            {/* New Product Specification Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                key={`${formInstanceKey}-materialType`}
                control={form.control}
                name="materialType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Material Type</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Cotton, Plastic, Steel"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                key={`${formInstanceKey}-size`}
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Size</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Small, XL, 2x3cm etc"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                key={`${formInstanceKey}-color`}
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. RED, BLUE etc"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* DEPRECATED: Grade/Quality field removed */}

              <FormField
                key={`${formInstanceKey}-shelfLife`}
                control={form.control}
                name="shelfLife"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shelf Life</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. None, 1year, 3months etc"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* DEPRECATED: Other Specification field removed */}

            {/* Required API Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cost field moved to Pricing & Quantity step */}

              <FormField
                key={`${formInstanceKey}-weight`}
                control={form.control}
                name="weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weight (kg)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter weight in kg"
                        value={
                          field.value === undefined ||
                          field.value === null ||
                          field.value === 0
                            ? ""
                            : String(field.value)
                        }
                        onChange={(e) => {
                          const value = e.target.value.trim();
                          // Store number - use undefined for empty to match backend validation (must be positive)
                          const numValue =
                            value === ""
                              ? undefined
                              : parseFloat(value) || undefined;
                          field.onChange(numValue);
                        }}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* DEPRECATED: Dimensions field removed */}

              {/* DEPRECATED: Stock Quantity field removed */}
              {/* DEPRECATED: Minimum Stock field removed */}
            </div>

            <FormField
              key={`${formInstanceKey}-description`}
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <RichTextEditor
                      key={`description-${product?.productId || isEdit ? "edit" : "new"}-${formInstanceKey}`}
                      placeholder="Enter product description"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      maxLength={2000}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Hidden companyId field for admin mode */}
            {isAdminMode && (
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }) => (
                  <FormItem className="hidden">
                    <FormControl>
                      <Input
                        type="hidden"
                        value={field.value ?? companyId ?? "default-company-id"}
                        onChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {/* DEPRECATED: Keywords/Tags field removed */}
          </div>
        );

      case "pricing":
        return (
          <div className="space-y-6">
            {/* Unit and Unit Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                key={`${formInstanceKey}-unitOfMeasure`}
                control={form.control}
                name="unitOfMeasure"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <Select
                      value={field.value ?? DEFAULT_PRODUCT_UNIT}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white max-h-[min(320px,50vh)] overflow-y-auto">
                        {getProductUnitSelectOptions(field.value).map(
                          (option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <FormField
                key={`${formInstanceKey}-price`}
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit price <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter product price"
                        value={
                          field.value === undefined ||
                          field.value === null ||
                          field.value === 0
                            ? ""
                            : String(field.value)
                        }
                        onChange={(e) => {
                          const value = e.target.value.trim();
                          // Store number - use undefined for empty to match backend validation (must be positive)
                          const numValue =
                            value === ""
                              ? undefined
                              : parseFloat(value) || undefined;
                          field.onChange(numValue);
                        }}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}

              <FormField
                key={`${formInstanceKey}-price`}
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Unit price <span className="text-red-500">*</span>
                    </FormLabel>

                    <FormControl>
                      <Input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder="Enter product price"
                        value={getPriceInputDisplay(
                          PRICE_DRAFT_MAIN,
                          field.value as number | undefined,
                        )}
                        onFocus={() => {
                          setPriceDraft(
                            PRICE_DRAFT_MAIN,
                            formatStoredPriceForDisplay(
                              field.value as number | undefined,
                            ),
                          );
                        }}
                        onChange={(e) => {
                          const sanitized = sanitizeTierPriceInput(
                            e.target.value,
                          );
                          setPriceDraft(PRICE_DRAFT_MAIN, sanitized);
                          field.onChange(tierPriceFromSanitized(sanitized));
                        }}
                        onKeyDown={(e) => {
                          if (e.ctrlKey || e.metaKey || e.altKey) return;
                          if (e.key.length === 1 && !/^[0-9.]$/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={() => {
                          const draft = priceInputDrafts[PRICE_DRAFT_MAIN];
                          const sanitized =
                            draft !== undefined
                              ? draft
                              : sanitizeTierPriceInput(
                                  formatStoredPriceForDisplay(
                                    field.value as number | undefined,
                                  ),
                                );
                          clearPriceDraft(PRICE_DRAFT_MAIN);
                          const final = roundProductPrice(
                            tierPriceFromSanitized(sanitized),
                          );
                          field.onChange(final);
                          field.onBlur();
                        }}
                        className="appearance-none"
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* DEPRECATED: Cost field removed */}
            </div>

            {/* DEPRECATED: Min Price and Max Price fields removed */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <FormField
                key={`${formInstanceKey}-currency`}
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select
                      value={field.value ?? "USD"}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white">
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="CNY">CNY</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                key={`${formInstanceKey}-moq`}
                control={form.control}
                name="moq"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Minimum order quantity (MOQ){" "}
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="Enter minimum order quantity"
                        value={getMoqInputDisplay(
                          field.value as number | undefined,
                        )}
                        onFocus={() => {
                          setPriceDraft(
                            MOQ_DRAFT_KEY,
                            formatStoredMoqForDisplay(
                              field.value as number | undefined,
                            ),
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.ctrlKey || e.metaKey || e.altKey) return;
                          if (e.key.length === 1 && !/^\d$/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const sanitized = sanitizeTierQuantityInput(
                            e.target.value,
                          );
                          setPriceDraft(MOQ_DRAFT_KEY, sanitized);
                          if (sanitized === "") {
                            field.onChange(undefined);
                            return;
                          }
                          const parsed = parseInt(sanitized, 10);
                          if (Number.isFinite(parsed)) {
                            field.onChange(parsed);
                          }
                        }}
                        onBlur={() => {
                          const draft = priceInputDrafts[MOQ_DRAFT_KEY];
                          const sanitized =
                            draft !== undefined
                              ? draft
                              : sanitizeTierQuantityInput(
                                  formatStoredMoqForDisplay(
                                    field.value as number | undefined,
                                  ),
                                );
                          clearPriceDraft(MOQ_DRAFT_KEY);
                          if (sanitized === "") {
                            field.onChange(undefined);
                          } else {
                            const parsed = parseInt(sanitized, 10);
                            field.onChange(
                              Number.isFinite(parsed) ? parsed : undefined,
                            );
                          }
                          field.onBlur();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Multiple Pricing */}
            <div className="space-y-4">
              <div className="flex items-center justify-between sticky top-0 bg-white z-10 pb-2">
                <label className="text-sm font-medium">Multiple Pricing</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTieredPrice}
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Price
                </Button>
              </div>

              {/* Column headers for tiered pricing */}
              {form.watch("tieredPricing")?.length ? (
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <FormLabel>Volume</FormLabel>
                  </div>
                  <div className="flex-1">
                    <FormLabel>Unit price</FormLabel>
                  </div>
                  <div className="w-10" aria-hidden />
                </div>
              ) : null}

              {form.watch("tieredPricing")?.map((tier, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="Enter volume"
                      value={(() => {
                        const v = getTierFieldValue(
                          index,
                          "quantity",
                          tier.quantity,
                        );
                        return v === "" ? "" : String(v);
                      })()}
                      onFocus={() => handleTierFieldFocus(index, "quantity")}
                      onBlur={handleTierFieldBlur}
                      onKeyDown={(e) => {
                        if (e.ctrlKey || e.metaKey || e.altKey) return;
                        if (e.key.length === 1 && !/^\d$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const sanitized = sanitizeTierQuantityInput(
                          e.target.value,
                        );
                        const tiered = [...form.getValues("tieredPricing")];
                        tiered[index].quantity =
                          sanitized === "" ? 0 : parseInt(sanitized, 10) || 0;
                        form.setValue("tieredPricing", tiered);
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="price"
                      value={getPriceInputDisplay(
                        priceDraftKeyTier(index),
                        tier.price,
                      )}
                      onFocus={() => {
                        handleTierFieldFocus(index, "price");
                        setPriceDraft(
                          priceDraftKeyTier(index),
                          formatStoredPriceForDisplay(tier.price),
                        );
                      }}
                      onBlur={() => {
                        const draftKey = priceDraftKeyTier(index);
                        const draft = priceInputDrafts[draftKey];
                        const tiered = [...form.getValues("tieredPricing")];
                        const sanitized =
                          draft !== undefined
                            ? draft
                            : sanitizeTierPriceInput(
                                formatStoredPriceForDisplay(
                                  tiered[index]?.price,
                                ),
                              );
                        tiered[index].price = roundProductPrice(
                          tierPriceFromSanitized(sanitized),
                        );
                        form.setValue("tieredPricing", tiered);
                        clearPriceDraft(draftKey);
                        handleTierFieldBlur();
                      }}
                      onKeyDown={(e) => {
                        if (e.ctrlKey || e.metaKey || e.altKey) return;
                        if (e.key.length === 1 && !/^[0-9.]$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const sanitized = sanitizeTierPriceInput(
                          e.target.value,
                        );
                        const draftKey = priceDraftKeyTier(index);
                        setPriceDraft(draftKey, sanitized);
                        const tiered = [...form.getValues("tieredPricing")];
                        tiered[index].price = tierPriceFromSanitized(sanitized);
                        form.setValue("tieredPricing", tiered);
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeTieredPrice(index)}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Payment Terms — FormField so setError/trigger can show FormMessage on save draft / Next */}
            <FormField
              control={form.control}
              name="paymentTerms"
              render={({ field }) => (
                <FormItem data-field="paymentTerms">
                  <FormLabel className="!text-gray-900">
                    Price Term / Payment method{" "}
                    <span className="text-red-500">*</span>
                  </FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      "T/T",
                      "L/C",
                      "Western Union",
                      "PayPal",
                      "Credit Card",
                      "Cash",
                      "D/P",
                      "D/A",
                    ].map((term) => (
                      <div key={term} className="flex items-center space-x-2">
                        <Checkbox
                          id={`payment-term-${term}`}
                          checked={field.value?.includes(term) ?? false}
                          onCheckedChange={() => togglePaymentTerm(term)}
                        />
                        <label
                          htmlFor={`payment-term-${term}`}
                          className="text-sm"
                        >
                          {term}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* DEPRECATED: Discount Price + Stock and Discount Date Range sections removed */}
          </div>
        );

      case "logistics":
        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="packaging"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Packing details <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe packaging details, materials, dimensions..."
                      rows={3}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* DEPRECATED: Package Details field removed */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="deliveryTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delivery Time</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. 7-15 days"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="shippingPort"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Shipping Port <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Shanghai, Ningbo"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Logistics Modes */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Logistics Modes</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  "Sea Freight",
                  "Air Freight",
                  "Express Delivery",
                  "Land Transport",
                  "Rail Transport",
                ].map((mode) => (
                  <div key={mode} className="flex items-center space-x-2">
                    <Checkbox
                      id={mode}
                      checked={form.watch("logisticsModes")?.includes(mode)}
                      onCheckedChange={() => toggleLogisticsMode(mode)}
                    />
                    <label htmlFor={mode} className="text-sm">
                      {mode}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* FOB/CIF Pricing */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Price Terms</label>
              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name="fobPrice"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">
                        FOB Price Available
                      </FormLabel>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cifPrice"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">
                        CIF Price Available
                      </FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Export Markets */}
            <div className="space-y-3">
              <label className="text-sm font-medium">
                Export Markets Served
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  "North America",
                  "Europe",
                  "Asia",
                  "South America",
                  "Africa",
                  "Oceania",
                  "Middle East",
                ].map((market) => (
                  <div key={market} className="flex items-center space-x-2">
                    <Checkbox
                      id={market}
                      checked={form.watch("exportMarkets")?.includes(market)}
                      onCheckedChange={() => toggleExportMarket(market)}
                    />
                    <label htmlFor={market} className="text-sm">
                      {market}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "media":
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-dashed border-gray-300 pb-2">
                Product Files & Media
              </h3>
            </div>

            <div className="space-y-6">
              {/* Gallery Images Section */}
              <div className="flex items-start space-x-4">
                <div className="w-32">
                  <label className="text-sm font-medium text-gray-700">
                    Gallery Images <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-gray-300 text-gray-700"
                      onClick={() => handleChooseFile("gallery")}
                    >
                      Choose File
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    These images are visible in product details page gallery.
                    Max 5 images, 2 MB each. Minimum dimensions: 900×900px.
                  </p>

                  {/* Selected Gallery Images Preview */}
                  {uploadedImages.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Selected Images ({uploadedImages.length})
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setUploadedImages([]);
                            form.setValue("images", []);
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Clear All
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                        {uploadedImages.map((imageItem, index) => (
                          <div key={imageItem.id} className="relative group">
                            <div className="aspect-square bg-gray-100 rounded border overflow-hidden">
                              <img
                                src={imageItem.thumbnailUrl || imageItem.url}
                                alt={
                                  imageItem.originalName ||
                                  `Gallery ${index + 1}`
                                }
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Fallback to a placeholder if image fails to load
                                  e.currentTarget.src =
                                    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y3ZjhmOSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+";
                                }}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setUploadedImages((prev) => {
                                  const updated = prev.filter(
                                    (_, i) => i !== index,
                                  );
                                  form.setValue(
                                    "images",
                                    updated.map((item) => item.url),
                                  );
                                  return updated;
                                });
                              }}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Thumbnail Image Section */}
              <div className="flex items-start space-x-4">
                <div className="w-32">
                  <label className="text-sm font-medium text-gray-700">
                    Thumbnail Image (300x300) (Optional)
                  </label>
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-gray-300 text-gray-700"
                      onClick={() => handleChooseFile("thumbnail")}
                    >
                      Choose File
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    If not provided, the first gallery image will be used as the
                    thumbnail. Minimum dimensions: 195×195px. Keep some blank
                    space around the main object for responsive cropping.
                  </p>

                  {/* Selected Thumbnail Image Preview */}
                  {thumbnailImage && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Selected Thumbnail
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setThumbnailImage(null);
                            form.setValue("thumbnailImage", "", {
                              shouldValidate: true,
                            });
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </div>
                      <div className="w-32 h-32 relative group">
                        <div className="aspect-square bg-gray-100 rounded border overflow-hidden">
                          <img
                            src={
                              thumbnailImage.thumbnailUrl || thumbnailImage.url
                            }
                            alt={thumbnailImage.originalName || "Thumbnail"}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src =
                                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y3ZjhmOSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+";
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setThumbnailImage(null);
                            form.setValue("thumbnailImage", "", {
                              shouldValidate: true,
                            });
                          }}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* DEPRECATED: Video Provider Section removed */}

              {/* Video Link Section */}
              <div className="flex items-start space-x-4">
                <div className="w-32">
                  <label className="text-sm font-medium text-gray-700">
                    Video Link
                  </label>
                </div>
                <div className="flex-1 space-y-2">
                  <Input
                    placeholder="Video Link"
                    className="w-96 border-gray-300"
                    value={uploadedVideo ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUploadedVideo(value);
                      form.setValue("videoUrl", value, {
                        shouldDirty: true,
                        shouldValidate: false,
                      });
                    }}
                  />
                  <p className="text-xs text-gray-500">
                    Use proper link without extra parameter. Don't use short
                    share link/embeded iframe code.
                  </p>
                </div>
              </div>

              {/* PDF Specification Section */}
              <div className="flex items-start space-x-4">
                <div className="w-32">
                  <label className="text-sm font-medium text-gray-700">
                    PDF Specification
                  </label>
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-gray-300 text-gray-700"
                      onClick={() => handleChooseFile("pdf")}
                    >
                      Choose File
                    </Button>
                  </div>

                  {/* Selected PDF Preview */}
                  {pdfSpecification && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Selected PDF
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPdfSpecification(null)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded border">
                        <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                          <FileText className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {pdfSpecification.originalName ||
                              "PDF Specification"}
                          </p>
                          <p className="text-xs text-gray-500">
                            Click to view or download
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPdfSpecification(null)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SEO Meta Tags Section */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-dashed border-gray-300 pb-2">
                  SEO Meta Tags
                </h3>

                <FormField
                  key={`${formInstanceKey}-metaTitle`}
                  control={form.control}
                  name="metaTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Meta Title
                        <span className="text-xs text-gray-500 ml-2 font-normal">
                          (uses product name, {String(form.watch("name") ?? "").length}/
                          {PRODUCT_NAME_MAX_LENGTH} characters)
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Meta Title"
                          readOnly
                          disabled
                          value={String(form.watch("name") ?? "").slice(
                            0,
                            PRODUCT_NAME_MAX_LENGTH,
                          )}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormDescription>
                        Meta title is automatically set from the product name for SEO.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  key={`${formInstanceKey}-metaDescription`}
                  control={form.control}
                  name="metaDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Meta Description
                        <span className="text-xs text-gray-500 ml-2 font-normal">
                          ({String(field.value ?? "").length}/
                          {PRODUCT_META_DESCRIPTION_MAX_LENGTH} characters)
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Meta Description"
                          rows={3}
                          maxLength={PRODUCT_META_DESCRIPTION_MAX_LENGTH}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value.slice(
                                0,
                                PRODUCT_META_DESCRIPTION_MAX_LENGTH,
                              ),
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Recommended length: 150 characters for search engine results.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Meta Keywords (comma-separated, stored as string array) */}
                <FormField
                  key={`${formInstanceKey}-metaKeywordsText`}
                  control={form.control}
                  name="metaKeywordsText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meta Keywords</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter keywords separated by commas"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            metaKeywordsTextRef.current = raw;
                            field.onChange(raw);
                            form.setValue(
                              "metaKeywords",
                              parseMetaKeywordsList(raw),
                              { shouldValidate: false },
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        Example: headphones, wireless, noise cancelling
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>
            </div>
          </div>
        );

      case "review":
        return (
          <div className="space-y-6">
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Product Preview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Product Card Preview */}
                <Card className="border-2 border-dashed border-orange-200">
                  <CardContent className="p-4">
                    <div className="aspect-square bg-muted rounded-lg mb-3 flex items-center justify-center">
                      {uploadedImages.length > 0 ? (
                        <img
                          src={uploadedImages[0].url}
                          alt={uploadedImages[0].originalName || "Product"}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <ImageIcon className="w-12 h-12 text-gray-500" />
                      )}
                    </div>
                    <h4 className="font-semibold text-sm mb-1">
                      {form.watch("name") || "Product Name"}
                    </h4>
                    <p className="text-xs text-gray-500 mb-2">
                      {form.watch("brand") || "Brand"}
                    </p>
                    <p className="text-sm font-medium text-orange-500">
                      ${form.watch("price") || 0} /{" "}
                      {getProductUnitLabel(form.watch("unitOfMeasure"))}
                    </p>
                    <p className="text-xs text-gray-500">
                      MOQ: {form.watch("moq") || "-"} /{" "}
                      {getProductUnitLabel(form.watch("unitOfMeasure"))}
                    </p>
                  </CardContent>
                </Card>

                {/* Summary Information */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Category:</span>
                      <p className="text-gray-500">
                        {form.watch("category") || "-"}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium">Brand:</span>
                      <p className="text-gray-500">
                        {form.watch("brand") || "-"}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium">MOQ:</span>
                      <p className="text-gray-500">
                        {form.watch("moq") || "-"} /{" "}
                        {getProductUnitLabel(form.watch("unitOfMeasure"))}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>
                      <Badge
                        variant={
                          form.watch("status") === "active"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {form.watch("status")}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Complete Summary */}
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Basic Information</h4>
                    <ul className="space-y-1 text-gray-500">
                      <li>Name: {form.watch("name") || "-"}</li>
                      <li>
                        Category:{" "}
                        {form.watch("category") ||
                          form.watch("categoryId") ||
                          "-"}
                      </li>
                      {form.watch("subcategory") && (
                        <li>Subcategory: {form.watch("subcategory")}</li>
                      )}
                      <li>Brand: {form.watch("brand") || "-"}</li>
                      {/* DEPRECATED: Model removed */}
                      {/* DEPRECATED: SKU removed */}
                      {/* DEPRECATED: Cost removed */}
                      {form.watch("weight") !== undefined &&
                        form.watch("weight") !== 0 && (
                          <li>Weight: {form.watch("weight")} kg</li>
                        )}
                      {/* DEPRECATED: Dimensions, Stock, Min Stock, HS Code, Keywords removed */}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Commercial Details</h4>
                    <ul className="space-y-1 text-gray-500">
                      <li>
                        Unit:{" "}
                        {form.watch("unitOfMeasure")
                          ? getProductUnitLabel(form.watch("unitOfMeasure"))
                          : "-"}
                      </li>
                      <li>Unit Price: ${form.watch("price") || "-"}</li>
                      <li>
                        MOQ: {form.watch("moq") || "-"} /{" "}
                        {getProductUnitLabel(form.watch("unitOfMeasure"))}
                      </li>
                      {/* DEPRECATED: Supply Ability per Month removed */}
                      {form.watch("tieredPricing") &&
                        form.watch("tieredPricing").length > 0 && (
                          <li>
                            Multiple Pricing:{" "}
                            {form
                              .watch("tieredPricing")
                              ?.map(
                                (tier: any) =>
                                  `${tier.quantity}@$${tier.price}`,
                              )
                              .join(", ")}
                          </li>
                        )}
                      <li>
                        Payment Terms:{" "}
                        {form.watch("paymentTerms")?.join(", ") || "None"}
                      </li>
                      {form.watch("discount") !== undefined &&
                        form.watch("discount") !== null && (
                          <li>Discount: ${form.watch("discount")}</li>
                        )}
                      {/* DEPRECATED: Discount fields removed */}
                    </ul>
                  </div>
                </div>

                {/* Product Specifications */}
                {(form.watch("materialType") ||
                  form.watch("size") ||
                  form.watch("color") ||
                  form.watch("grade") ||
                  form.watch("shelfLife") ||
                  form.watch("otherSpecification")) && (
                  <div>
                    <h4 className="font-medium mb-2">Product Specifications</h4>
                    <ul className="space-y-1 text-gray-500">
                      {form.watch("materialType") && (
                        <li>Material Type: {form.watch("materialType")}</li>
                      )}
                      {form.watch("size") && (
                        <li>Size: {form.watch("size")}</li>
                      )}
                      {form.watch("color") && (
                        <li>Color: {form.watch("color")}</li>
                      )}
                      {form.watch("grade") && (
                        <li>Grade: {form.watch("grade")}</li>
                      )}
                      {form.watch("shelfLife") && (
                        <li>Shelf Life: {form.watch("shelfLife")}</li>
                      )}
                      {form.watch("otherSpecification") && (
                        <li>Other: {form.watch("otherSpecification")}</li>
                      )}
                    </ul>
                  </div>
                )}

                {form.watch("description") && (
                  <div>
                    <h4 className="font-medium mb-2">Description</h4>
                    <p className="text-gray-500">{form.watch("description")}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Media & Documents</h4>
                    <ul className="space-y-1 text-gray-500">
                      <li>Gallery Images: {uploadedImages.length} uploaded</li>
                      <li>
                        Thumbnail:{" "}
                        {thumbnailImage
                          ? "Uploaded"
                          : "First gallery image will be used"}
                      </li>
                      <li>
                        Video:{" "}
                        {uploadedVideo || form.watch("videoUrl")
                          ? "Uploaded"
                          : "None"}
                      </li>
                      <li>
                        PDF Specification:{" "}
                        {pdfSpecification ? "Uploaded" : "None"}
                      </li>
                      <li>Documents: {uploadedDocuments.length} uploaded</li>
                      <li>
                        Certifications: {uploadedCertifications.length} uploaded
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Logistics</h4>
                    <ul className="space-y-1 text-gray-500">
                      <li>
                        Packaging: {form.watch("packaging") || "Not specified"}
                      </li>
                      {/* DEPRECATED: Package Details removed */}
                      <li>
                        Delivery Time:{" "}
                        {form.watch("deliveryTime") || "Not specified"}
                      </li>
                      <li>
                        Shipping Port:{" "}
                        {form.watch("shippingPort") || "Not specified"}
                      </li>
                      <li>
                        Logistics Modes:{" "}
                        {form.watch("logisticsModes")?.join(", ") || "None"}
                      </li>
                      <li>
                        FOB Price: {form.watch("fobPrice") ? "Yes" : "No"}
                      </li>
                      <li>
                        CIF Price: {form.watch("cifPrice") ? "Yes" : "No"}
                      </li>
                      <li>
                        Export Markets:{" "}
                        {form.watch("exportMarkets")?.join(", ") || "None"}
                      </li>
                    </ul>
                  </div>
                </div>

                {/* SEO */}
                {(form.watch("metaTitle") ||
                  form.watch("metaDescription") ||
                  String(form.watch("metaKeywordsText") ?? "").trim()) && (
                  <div>
                    <h4 className="font-medium mb-2">SEO</h4>
                    <ul className="space-y-1 text-gray-500">
                      {form.watch("metaTitle") && (
                        <li>Meta Title: {form.watch("metaTitle")}</li>
                      )}
                      {form.watch("metaDescription") && (
                        <li>
                          Meta Description: {form.watch("metaDescription")}
                        </li>
                      )}
                      {String(form.watch("metaKeywordsText") ?? "").trim() && (
                        <li>
                          Meta Keywords:{" "}
                          {String(form.watch("metaKeywordsText") ?? "").trim()}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Form content that can be rendered with or without Dialog wrapper
  const formContent = (
    <div
      className={`min-w-0 w-full ${renderAsPage ? "flex flex-col min-h-0 flex-1" : "flex h-[85vh]"}`}
    >
      {/* Alibaba-style: Horizontal Step Indicator at Top (only for page mode) */}
      {renderAsPage && (
        <>
          <div
            ref={pageStepHeaderRef}
            className="border-b bg-white px-3 py-3 sm:px-4 sm:py-4 md:px-6 lg:px-8 min-w-0 shrink-0"
          >
            <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 min-w-0 break-words pr-2">
                {getSteps(isAdminMode)[currentStep].title}
              </h2>
              <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0 whitespace-nowrap pt-0.5">
                Step {currentStep + 1} of {getSteps(isAdminMode).length}
              </span>
            </div>
            {/* Wrap onto multiple lines when narrow; connector + step stay one unit so lines never orphan. */}
            <div className="w-full min-w-0 -mx-3 px-3 pb-2 sm:-mx-4 sm:px-4 md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
              {(() => {
                const pageSteps = getSteps(isAdminMode);
                return (
                  <ol className="m-0 flex list-none flex-wrap items-center gap-x-2 gap-y-3 p-0 sm:gap-x-3 sm:gap-y-3.5">
                    {pageSteps.map((step, index) => {
                      const Icon = step.icon;
                      const isCurrentStep = currentStep === index;
                      const isCompleted = validatedSteps.has(step.id);
                      const isPast = index < currentStep;
                      const prevStep = index > 0 ? pageSteps[index - 1] : null;
                      const connectorComplete =
                        index > 0 &&
                        (index - 1 < currentStep ||
                          (prevStep ? validatedSteps.has(prevStep.id) : false));

                      return (
                        <li
                          key={step.id}
                          className="flex min-w-0 max-w-full shrink-0 items-center"
                        >
                          {index > 0 ? (
                            <span
                              className={`mr-2 inline-block h-0.5 w-5 shrink-0 self-center rounded-full sm:mr-2.5 sm:w-8 md:w-10 lg:w-12 ${
                                connectorComplete
                                  ? "bg-green-500"
                                  : "bg-gray-200"
                              }`}
                              aria-hidden
                            />
                          ) : null}
                          <div className="flex min-w-0 max-w-full items-center gap-1.5 sm:gap-2">
                            <div
                              className={`flex shrink-0 items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 transition-colors ${
                                isCurrentStep
                                  ? "bg-orange-500 border-orange-500 text-white"
                                  : isCompleted || isPast
                                    ? "bg-green-500 border-green-500 text-white"
                                    : "bg-white border-gray-300 text-gray-400"
                              }`}
                              style={{ aspectRatio: "1 / 1" }}
                              aria-current={isCurrentStep ? "step" : undefined}
                            >
                              {isCompleted && !isCurrentStep ? (
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                              ) : (
                                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              )}
                            </div>
                            <span
                              className={`min-w-0 text-left text-xs font-medium leading-snug break-words sm:text-sm ${
                                isCurrentStep
                                  ? "text-orange-600"
                                  : isCompleted || isPast
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }`}
                            >
                              {step.title}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Sidebar Navigation (only for dialog mode) */}
      {!renderAsPage && (
        <div className="w-64 border-r bg-muted/30 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">
              {isEdit ? "Edit Product" : "Add New Product"}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isEdit
                ? "Update your product information"
                : "Create a new product for your catalog"}
            </p>
          </div>

          <nav className="space-y-2">
            {getSteps(isAdminMode).map((step, index) => {
              const Icon = step.icon;
              const isCurrentStep = currentStep === index;
              const isCompleted = validatedSteps.has(step.id);

              // Check if all previous steps are validated
              const previousSteps = getSteps(isAdminMode).slice(0, index);
              const allPreviousValid = previousSteps.every((prevStep) =>
                validatedSteps.has(prevStep.id),
              );
              const canNavigate =
                !wizardLocked && (index === 0 || allPreviousValid);

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => {
                    if (canNavigate) {
                      setCurrentStep(index);
                    }
                  }}
                  disabled={!canNavigate}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                    isCurrentStep
                      ? "bg-orange-500 text-white"
                      : !canNavigate
                        ? "opacity-50 cursor-not-allowed bg-gray-100"
                        : "hover:bg-orange-50"
                  }`}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5" />
                    {isCompleted && !isCurrentStep && (
                      <CheckCircle className="w-4 h-4 absolute -top-1 -right-1 text-green-500 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-sm font-medium">{step.title}</span>
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {!renderAsPage && (
          <div className="border-b p-6">
            <h3 className="text-lg font-semibold">
              {getSteps(isAdminMode)[currentStep].title}
            </h3>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-gray-500">
                Step {currentStep + 1} of {getSteps(isAdminMode).length}
              </span>
              <div className="flex-1 bg-muted rounded-full h-2">
                <div
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${((currentStep + 1) / getSteps(isAdminMode).length) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <div
          ref={renderAsPage ? pageFormScrollRef : undefined}
          className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 ${
            renderAsPage ? "p-4 sm:p-6 md:p-8" : "p-6"
          }`}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              {renderStepContent()}
            </form>
          </Form>
        </div>

        {/* Footer Actions - Only for dialog mode */}
        {!renderAsPage && (
          <div className="border-t p-6 bg-background">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                {currentStep < getSteps(isAdminMode).length - 1 &&
                  (getSteps(isAdminMode)[currentStep]?.id !== "basic" ||
                    (form.watch("description")?.trim()?.length ?? 0) > 0) && (
                    <Button
                      type="button"
                      onClick={nextStep}
                      variant="primary"
                      disabled={!currentStepValid}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
              </div>

              <div className="flex items-center gap-2">
                {isEdit && onArchive && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => {
                      onArchive();
                      setOpen(false);
                    }}
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archive Product
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={saveAsDraft}
                  disabled={isLoading || internalLoading}
                >
                  {isLoading || internalLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save as Draft
                </Button>

                <Button
                  type="button"
                  onClick={publishProduct}
                  variant="primary"
                  disabled={isLoading || internalLoading || !isPublishEnabled}
                >
                  {isLoading || internalLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  {isEdit ? "Update Product" : "Publish Product"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // If rendering as page, return content directly without Dialog wrapper
  if (renderAsPage) {
    return (
      <>
        {formContent}
        {/* Media Selection Modal */}
        <Dialog open={isMediaModalOpen} onOpenChange={setIsMediaModalOpen}>
          <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden bg-white border-2 border-orange-200 shadow-2xl mx-4 my-4">
            {/* <DialogHeader className="bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200 p-6">
              <DialogTitle className="text-xl font-semibold text-orange-900">
                {currentMediaType === "gallery" && "Select Gallery Images"}
                {currentMediaType === "thumbnail" && "Select Thumbnail Image"}
                {currentMediaType === "pdf" && "Select PDF Specification"}
                {currentMediaType === "metaImage" && "Select Meta Image"}
              </DialogTitle>
              <DialogDescription className="text-orange-700 mt-2">
                Choose from existing media or upload new files for your product.
              </DialogDescription>
            </DialogHeader> */}
            <div className="overflow-y-auto max-h-[70vh] bg-white p-6">
              <MediaSelector
                key={`media-selector-${isAdminMode && selectedSellerId ? selectedSellerId : "admin"}-${currentMediaType}`}
                selectedMedia={
                  currentMediaType === "gallery"
                    ? uploadedImages
                    : currentMediaType === "thumbnail"
                      ? thumbnailImage
                        ? [thumbnailImage]
                        : []
                      : pdfSpecification
                        ? [pdfSpecification]
                        : []
                }
                onMediaChange={(mediaItems, shouldCloseModal) =>
                  handleMediaSelection(mediaItems, shouldCloseModal)
                }
                mediaType={currentMediaType === "pdf" ? "pdf" : "image"}
                isAdminMode={isAdminMode}
                maxSelection={
                  currentMediaType === "thumbnail" ||
                  currentMediaType === "pdf"
                    ? 1
                    : 5
                }
                maxFileSizeBytes={
                  currentMediaType === "gallery" ? 2 * 1024 * 1024 : undefined
                }
                className="w-full"
                sellerId={
                  isAdminMode &&
                  selectedSellerId &&
                  selectedSellerId.trim().length > 0
                    ? selectedSellerId
                    : undefined
                }
                onCancel={() => handleMediaSelection([])}
                deferCommitUntilAdd={currentMediaType === "gallery"}
                modalOpen={isMediaModalOpen}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Seller Selection Dialog */}
        {isAdminMode && (
          <SellerSelectionDialog
            open={isSellerDialogOpen}
            onOpenChange={setIsSellerDialogOpen}
            selectedSellerId={selectedSellerId}
            onSelect={(sellerId, seller) => {
              setSelectedSellerId(sellerId);
              setSelectedSeller(seller);
              setIsSellerDialogOpen(false);
            }}
            companyId={companyId}
          />
        )}
      </>
    );
  }

  // Default: render with Dialog wrapper
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className="max-w-6xl max-h-[90vh] overflow-hidden p-0 bg-white"
        showCloseButton={false}
      >
        {formContent}
      </DialogContent>

      {/* Media Selection Modal */}
      <Dialog open={isMediaModalOpen} onOpenChange={setIsMediaModalOpen}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden bg-white border-2 border-orange-200 shadow-2xl mx-4 my-4">
          {/* <DialogHeader className="bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200 p-6">
            <DialogTitle className="text-xl font-semibold text-orange-900">
              {currentMediaType === "gallery" && "Select Gallery Images"}
              {currentMediaType === "thumbnail" && "Select Thumbnail Image"}
              {currentMediaType === "pdf" && "Select PDF Specification"}
              {currentMediaType === "metaImage" && "Select Meta Image"}
            </DialogTitle>
            <DialogDescription className="text-orange-700 mt-2">
              Choose from existing media or upload new files for your product.
            </DialogDescription>
          </DialogHeader> */}
          <div className="overflow-y-auto max-h-[70vh] bg-white p-6">
            <MediaSelector
              key={`media-selector-${isAdminMode && selectedSellerId ? selectedSellerId : "admin"}-${currentMediaType}`}
              selectedMedia={
                currentMediaType === "gallery"
                  ? uploadedImages
                  : currentMediaType === "thumbnail"
                    ? thumbnailImage
                      ? [thumbnailImage]
                      : []
                    : pdfSpecification
                      ? [pdfSpecification]
                      : []
              }
              onMediaChange={(mediaItems, shouldCloseModal) =>
                handleMediaSelection(mediaItems, shouldCloseModal)
              }
              mediaType={currentMediaType === "pdf" ? "pdf" : "image"}
              isAdminMode={isAdminMode}
              maxSelection={
                currentMediaType === "thumbnail" ||
                currentMediaType === "pdf"
                  ? 1
                  : 5
              }
              maxFileSizeBytes={
                currentMediaType === "gallery" ? 2 * 1024 * 1024 : undefined
              }
              className="w-full"
              sellerId={
                isAdminMode &&
                selectedSellerId &&
                selectedSellerId.trim().length > 0
                  ? selectedSellerId
                  : undefined
              }
              onCancel={() => handleMediaSelection([])}
              deferCommitUntilAdd={currentMediaType === "gallery"}
              modalOpen={isMediaModalOpen}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Seller Selection Dialog */}
      {isAdminMode && (
        <SellerSelectionDialog
          open={isSellerDialogOpen}
          onOpenChange={setIsSellerDialogOpen}
          selectedSellerId={selectedSellerId}
          onSelect={(sellerId, seller) => {
            setSelectedSellerId(sellerId);
            setSelectedSeller(seller);
            // Close the dialog after selection
            setIsSellerDialogOpen(false);
          }}
          companyId={companyId}
        />
      )}
    </Dialog>
  );
}
